// src/fees/index.js — екран „Такси и плащания" (СТРОГО ПОВЕРИТЕЛЕН).
//
// ЗАЩО го има: таксите на кантората и кой колко е платил се водеха в отделна
// таблица извън системата. Тук са на едно място — но това са вътрешни пари на
// кантората, не данни на клиентите, затова разделът е само за собственика.
//
// ВНИМАНИЕ: скриването тук е КОЗМЕТИКА. Истинската защита е `ownerRequired`
// на сървъра (server.js монтира /api/fees през него). Ако някой ден проверката
// отдолу падне, скритият бутон няма да спре никого.
//
// Данните идват от:
//   GET    /api/fees?year=YYYY        — клиенти, месеци, дълг, общо
//   PUT    /api/fees/payments         — плащане за клетка (клиент × месец)
//   POST   /api/fees/clients          — нов клиент
//   PUT    /api/fees/clients/:id      — редакция (вкл. таксите D и E)
//   DELETE /api/fees/clients/:id      — изтриване
//   PUT    /api/fees/carryover        — пренос от минала година
//   POST   /api/fees/adjustments      — еднократна корекция (дата, сума, защо)
//   DELETE /api/fees/adjustments/:id  — сгрешена корекция
//   GET    /api/fees/analytics?year=  — справките
//
// ВСИЧКИ суми по API-то са в ЕВРОЦЕНТОВЕ (цели числа). В целия файл преходът
// „поле ↔ центове" минава САМО през toCents()/fromCents(), а показването —
// само през money(). Едно преобразуване за целия екран: две биха се разминали
// някой ден (същият урок като normalizeId на сървъра).

import { el, clear, $, field, input, clickableRow } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, statCard, searchInput, tabBar } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state } from '../shared/state.js';
// Датите минават през общите помощници: native <input type="date"> е забранен
// в проекта (различен вид и различен формат във всеки браузър).
import { dateInput, isoToBg } from '../shared/dates.js';
import { printFeesTable } from './print.js';
import {
  exportFeesMatrix, exportExpenses, exportProfit, exportAccountantMonths,
  exportAccountantsBoard, exportExpensesByPerson,
} from './xlsx.js';

const MONTHS_SHORT = ['Ян', 'Фев', 'Мар', 'Апр', 'Май', 'Юни', 'Юли', 'Авг', 'Сеп', 'Окт', 'Ное', 'Дек'];
const MONTHS_FULL = ['Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни', 'Юли',
  'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];

// Годишното приключване няма месец. 0 = „за цялата година" — същата уговорка
// като в работната бланка (таб „Годишни" също праща month: 0).
const ANNUAL_MONTH = 0;

// Състояние, което се пази между рендерите (както филтрите на бланката).
let _year = new Date().getFullYear();
let _tab = 'matrix'; // 'matrix' | 'analytics'

const isOwner = () => !!(state().user && state().user.owner === true);

// ---- Кои колони се виждат ---------------------------------------------------
//
// ЗАЩО: матрицата е 21 колони (клиент, подпис, счетоводител, две такси, пренос,
// дванайсет месеца, корекции, дълг) и не се събира на един екран. Затова всяка
// група колони може да се изключи — както в екселс. Изборът се пази в
// localStorage (същият похват като режима на менюто в src/shell/shell.js), за
// да не се нагласява наново при всяко отваряне.
//
// „Клиент" и „Дълг" НЕ са в този списък и не могат да се скрият: без тях
// таблицата спира да отговаря на въпроса, заради който се отваря — кой колко
// дължи.
// Подписите (кой води фирмата). В тясната колона стоят само инициалите, а в
// падащите менюта — пълното име, ако е попълнено тук. Празно име = показва се
// само кодът, тоест системата работи и без имената (В.1).
const SIGNATURES = [
  { code: 'БВ', name: '' },
  { code: 'ДВ', name: '' },
];
function sigLabel(code) {
  const s = SIGNATURES.find((x) => x.code === code);
  return (s && s.name) ? (s.code + ' — ' + s.name) : String(code || '');
}

const COLS_KEY = 'feesColumns';
const COL_GROUPS = [
  { id: 'signature', label: 'Подпис', span: 1 },
  { id: 'accountant', label: 'Счетоводител („Работи го")', span: 1 },
  { id: 'fees', label: 'Основна и ДДС такса (D и E)', span: 2 },
  { id: 'carry', label: 'Пренос', span: 1 },
  { id: 'adj', label: 'Корекции', span: 1 },
];

function defaultCols() {
  const c = { months: new Array(12).fill(true) };
  COL_GROUPS.forEach((g) => { c[g.id] = true; });
  return c;
}

// Записът се чете ЗАЩИТЕНО: стар, чужд или повреден JSON не бива да оставя
// екрана без колони. Само изричното false крие — всичко друго значи „показва се".
function readCols() {
  const c = defaultCols();
  let raw = null;
  try { raw = JSON.parse(localStorage.getItem(COLS_KEY) || 'null'); } catch (e) { raw = null; }
  if (!raw || typeof raw !== 'object') return c;
  COL_GROUPS.forEach((g) => { if (raw[g.id] === false) c[g.id] = false; });
  if (Array.isArray(raw.months)) {
    for (let i = 0; i < 12; i++) if (raw.months[i] === false) c.months[i] = false;
  }
  return c;
}

let _cols = readCols();

function saveCols() { try { localStorage.setItem(COLS_KEY, JSON.stringify(_cols)); } catch (e) {} }

// ЕДНО място за въпроса „вижда ли се тази колона". Ако thead, редовете и tfoot
// решават всеки за себе си, рано или късно се разминават и сборовете увисват
// под ЧУЖДИ месеци — числата на екрана започват да лъжат.
function colOn(id) { return _cols[id] !== false; }

function visibleMonths() {
  const out = [];
  for (let m = 1; m <= 12; m++) if (_cols.months[m - 1] !== false) out.push(m);
  return out;
}

// Колоните ПРЕДИ месеците (Клиент + видимите от групите) и СЛЕД тях (Корекции +
// Дълг). Редът със сборовете стъпва на тях през colspan — затова се смятат тук,
// а не се пишат като число на две места.
function leadSpan() {
  let n = 1; // „Клиент" винаги е първа
  COL_GROUPS.forEach((g) => { if (g.id !== 'adj' && colOn(g.id)) n += g.span; });
  return n;
}
function tailSpan() { return (colOn('adj') ? 1 : 0) + 1; } // + „Дълг"

// ---- Какво показва справката „по счетоводител и месец" ----------------------
//
// 'paid' = постъпилото, 'expected' = начисленото. Досега начисленото стоеше
// само в title — на телефон то изобщо не се вижда, а собственикът гледа точно
// него, когато проверява дали е фактурирал всичко.
const ACC_MODE_KEY = 'feesAnalyticsMode';
function readAccMode() {
  try { return localStorage.getItem(ACC_MODE_KEY) === 'expected' ? 'expected' : 'paid'; }
  catch (e) { return 'paid'; }
}
let _accMode = readAccMode();
function saveAccMode() { try { localStorage.setItem(ACC_MODE_KEY, _accMode); } catch (e) {} }

// ---- Подредбата на таблото „Счетоводители" ---------------------------------
//
// ЗАЩО се помни: собственикът отваря таблото с един и същ въпрос всеки ден
// („кой носи най-много", „кой струва най-много"). Записът е ЕДИН низ
// „колона:посока" — два отделни ключа биха могли да се разпаднат наполовина
// (записан ключ без посока) и екранът да тръгне с половин избор.
const ACC_SORT_KEY = 'feesAccountantsSort';
// „paid" ГО НЯМА нарочно: колоната „Платено" отпадна по искане на собственика.
// Стар запис „paid:desc" пада в подредбата по подразбиране (проверката отдолу) —
// затова списъкът е и филтърът за заварени записи, не само за повредени.
const BOARD_SORTABLE = ['name', 'clients', 'expected', 'direct', 'shared', 'cost', 'left'];
function readAccSort() {
  let raw = '';
  try { raw = String(localStorage.getItem(ACC_SORT_KEY) || ''); } catch (e) { raw = ''; }
  const parts = raw.split(':');
  // Непозната колона (стар или повреден запис) не бива да остави таблото
  // несортирано — връщаме подредбата по подразбиране: най-НАЧИСЛЕНОТО отгоре.
  // То е числото, което остана да отговаря на „кой носи най-много".
  if (BOARD_SORTABLE.indexOf(parts[0]) < 0) return { key: 'expected', dir: 'desc' };
  return { key: parts[0], dir: parts[1] === 'asc' ? 'asc' : 'desc' };
}
let _accSort = readAccSort();
function saveAccSort() { try { localStorage.setItem(ACC_SORT_KEY, _accSort.key + ':' + _accSort.dir); } catch (e) {} }

// ---- „Разходи по хора": за месец или за годината ---------------------------
//
// ЗАЩО двата изгледа: заплатите се гледат с два различни въпроса — „колко ми
// струва човекът ТОЗИ месец" (сверява се срещу платежното) и „колко за
// годината досега" (годишната сметка). Едно число не отговаря на двата.
// Изборът се помни, както всичко останало на този екран.
const PERSON_SCOPE_KEY = 'feesPersonScope';
const PERSON_MONTH_KEY = 'feesPersonMonth';
function readPersonScope() {
  try { return localStorage.getItem(PERSON_SCOPE_KEY) === 'month' ? 'month' : 'year'; }
  catch (e) { return 'year'; }
}
function readPersonMonth() {
  let v = null;
  try { v = localStorage.getItem(PERSON_MONTH_KEY); } catch (e) { v = null; }
  // null значи „още не е избиран" — тогава месецът се решава от through_month
  // при рисуването, а не се измисля тук.
  return monthOrNull(v);
}
let _personScope = readPersonScope();
let _personMonth = readPersonMonth();
function savePersonScope() {
  try {
    localStorage.setItem(PERSON_SCOPE_KEY, _personScope);
    if (_personMonth) localStorage.setItem(PERSON_MONTH_KEY, String(_personMonth));
  } catch (e) {}
}

// ---- Подтабове в „Анализи" -------------------------------------------------
//
// ЗАЩО: справките станаха три карти и десетина панела един под друг — долните
// се стигаха само с дълго превъртане и на практика не се гледаха. Затова са
// групирани по ВЪПРОС, не по произход на данните:
//   „Пари"          — колко влиза, колко излиза, какво остава (и разходите);
//   „Клиенти"       — кой дължи, кой е надплатил, как е по подпис;
//   „Счетоводители" — какво носи и колко струва всеки човек.
// Изборът се помни (същият похват като колоните и филтрите), защото
// собственикът гледа едно и също всеки ден.
const ANA_TAB_KEY = 'feesAnalyticsTab';
const ANA_TABS = [
  { id: 'money', label: 'Пари', hint: 'Приход, разход, печалба и записаните разходи.' },
  { id: 'clients', label: 'Клиенти', hint: 'Кой дължи, кой е надплатил и разбивката по подпис.' },
  { id: 'accountants', label: 'Счетоводители', hint: 'Какво носи и колко струва всеки счетоводител.' },
];

// Чете се ЗАЩИТЕНО (както readCols/readFilters): непознат или повреден запис
// връща първия подтаб, вместо да остави екрана без нито един панел.
function readAnaTab() {
  let v = null;
  try { v = localStorage.getItem(ANA_TAB_KEY); } catch (e) { v = null; }
  return ANA_TABS.some((t) => t.id === v) ? v : 'money';
}
let _anaTab = readAnaTab();
function saveAnaTab() { try { localStorage.setItem(ANA_TAB_KEY, _anaTab); } catch (e) {} }

// ---- Филтри над матрицата ---------------------------------------------------
//
// ЗАЩО: таблицата е 91 реда. Без филтър въпросът „кой от клиентите на Мария
// дължи" се отговаря с превъртане и пръст по екрана. Филтрите са същите като в
// екселс — име, подпис, счетоводител, състояние — и се ПОМНЯТ (същият похват
// като избора на колони), защото собственикът гледа едно и също всеки ден.
//
// ВАЖНО: филтърът важи за месечната матрица. „Годишно приключване" е отделна
// таблица с друга логика (еднократна такса) и си остава пълна — иначе човек би
// решил, че годишните клиенти са изчезнали.
const FILTERS_KEY = 'feesFilters';

// „Без счетоводител" е СТОЙНОСТ на филтъра, не празно: празното вече значи
// „всички". Затова има отделен ключ, който никое име не може да заеме.
const NO_ACCOUNTANT = '__none__';

const STATE_FILTERS = ['debt', 'settled', 'overpaid'];

function defaultFilters() { return { q: '', sig: '', acc: '', state: 'all' }; }

// Чете се ЗАЩИТЕНО (както readCols): стар или повреден запис не бива да остави
// екрана с филтър, който човек не може да намери и махне.
function readFilters() {
  const f = defaultFilters();
  let raw = null;
  try { raw = JSON.parse(localStorage.getItem(FILTERS_KEY) || 'null'); } catch (e) { raw = null; }
  if (!raw || typeof raw !== 'object') return f;
  if (typeof raw.q === 'string') f.q = raw.q.trim().toLowerCase();
  if (raw.sig === 'БВ' || raw.sig === 'ДВ') f.sig = raw.sig;
  if (typeof raw.acc === 'string') f.acc = raw.acc;
  if (STATE_FILTERS.indexOf(raw.state) >= 0) f.state = raw.state;
  return f;
}

let _filters = readFilters();
function saveFilters() { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(_filters)); } catch (e) {} }

// ---- Клиентите на матрицата, ползвани и в „Анализи" ------------------------
//
// ЗАЩО се пази списъкът: таблото на счетоводителите отговаря и на въпроса
// „колко клиента води всеки", а това го знае САМО списъкът от матрицата.
// Екранът винаги тръгва от таб „Такси", тоест списъкът вече е в паметта —
// второ дърпане на същия адрес би било чиста загуба.
//
// Годината пътува със записа НАРОЧНО: брой клиенти от МИНАЛАТА година, показан
// до тазгодишни суми, е по-лош от липсващ брой — изглежда като истина.
let _clientsCache = { year: null, clients: null };
function rememberClients(year, clients) {
  _clientsCache = { year: Number(year), clients: Array.isArray(clients) ? clients : [] };
}
function cachedClients(year) {
  return (_clientsCache.year === Number(year) && _clientsCache.clients) ? _clientsCache.clients : null;
}

// ЕДНО място за въпроса „филтрирано ли е". И редовете, и сборовете, и печатът
// стъпват на него — ако се разминат, екранът показва едни редове и сумира други.
function filterActive() {
  return !!(_filters.q || _filters.sig || _filters.acc || _filters.state !== 'all');
}

function matchesFilters(c) {
  if (_filters.q && String(c.label || '').toLowerCase().indexOf(_filters.q) < 0) return false;
  if (_filters.sig && (c.signature || '') !== _filters.sig) return false;
  if (_filters.acc) {
    const acc = String(c.accountant_name || '').trim();
    if (_filters.acc === NO_ACCOUNTANT ? acc !== '' : acc !== _filters.acc) return false;
  }
  // Състоянието се чете от debt_cents на СЪРВЪРА — екранът не смята дълг сам.
  const debt = Number(c.debt_cents) || 0;
  if (_filters.state === 'debt' && !(debt > 0)) return false;
  if (_filters.state === 'settled' && debt !== 0) return false;
  if (_filters.state === 'overpaid' && !(debt < 0)) return false;
  return true;
}

// Месечните клиенти на текущите данни — на едно място, защото се пита от
// матрицата, от сборовете и от печата.
const monthlyOf = (data) => ((data && data.clients) || []).filter((c) => c.kind !== 'annual');

// Центове → пари за четене. money() е ЕДИНСТВЕНОТО форматиране на суми.
function eur(cents) { return money((Number(cents) || 0) / 100, 'EUR'); }

// Поле → центове. Приема и „12,50", и „12.50" (човек пише със запетая, а
// клавиатурата дава точка). Празно е НУЛА: изчистена клетка значи „няма
// плащане", а не „не знам". null = не е сума и нищо не се записва.
export function toCents(v) {
  const s = String(v == null ? '' : v).replace(/[\s ]/g, '').replace(',', '.');
  if (s === '') return 0;
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

// Центове → поле. Нулата се показва като ПРАЗНО: таблица с 300 реда „0,00"
// е нечетима, а окото търси кои клетки са платени.
export function fromCents(cents) {
  const n = Number(cents) || 0;
  if (!n) return '';
  return (n / 100).toFixed(2).replace('.', ',');
}

// Днес в ISO. НЕ през toISOString(): той минава през UTC и след 3 ч. следобед
// у нас връща вчерашния ден — новата корекция би тръгнала с грешна дата.
function todayIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

// ---- Период на начисляване (start_month / end_month) ----------------------
// Клиент, дошъл през май, не се начислява от януари; спрян през септември —
// не до декември. И двете са по избор: липсват = целогодишно.

// Едно преобразуване за целия екран: празно, null, 0 и глупост стават null.
export function monthOrNull(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : null;
}

// Начислява ли се месец m на този клиент.
export function monthApplies(c, m) {
  const s = monthOrNull(c && c.start_month);
  const e = monthOrNull(c && c.end_month);
  if (s && m < s) return false;
  if (e && m > e) return false;
  return true;
}

// „от Май", „до Септември", „Май – Септември" — или нищо при целогодишен.
function periodLabel(c) {
  const s = monthOrNull(c && c.start_month);
  const e = monthOrNull(c && c.end_month);
  if (s && e) return MONTHS_FULL[s - 1] + ' – ' + MONTHS_FULL[e - 1];
  if (s) return 'от ' + MONTHS_FULL[s - 1];
  if (e) return 'до ' + MONTHS_FULL[e - 1];
  return '';
}

// Защо клетката е неприложима — с думи, не само с цвят.
function periodReason(c) {
  const s = monthOrNull(c && c.start_month);
  const e = monthOrNull(c && c.end_month);
  if (s && e) return 'Клиентът се начислява само от ' + MONTHS_FULL[s - 1] + ' до ' + MONTHS_FULL[e - 1] + ' — този месец не се дължи.';
  if (s) return 'Клиентът се начислява от ' + MONTHS_FULL[s - 1] + ' нататък — този месец не се дължи.';
  if (e) return 'Клиентът се начислява до ' + MONTHS_FULL[e - 1] + ' — този месец не се дължи.';
  return 'Този месец не се начислява на клиента.';
}

// ---------------------------------------------------------------------------

export async function view(page) {
  clear(page);
  // Изборът на колони и „платено/начислено" се чете при ВСЯКО отваряне на
  // екрана: истината е записът в localStorage, не паметта на модула. Иначе
  // втори раздел в браузъра би показвал друга таблица от същия запис.
  _cols = readCols();
  _accMode = readAccMode();
  _anaTab = readAnaTab();
  _filters = readFilters();
  _accSort = readAccSort();
  _personScope = readPersonScope();
  _personMonth = readPersonMonth();
  // Проверката е ПРЕДИ всяка заявка: който не е собственик, не бива дори да
  // пита сървъра — иначе получава гол отказ, който нищо не обяснява.
  if (!isOwner()) {
    page.appendChild(el('div', { class: 'panel' },
      emptyState('€', 'Разделът „Такси и плащания" е достъпен само за собственика на кантората.')));
    return;
  }
  const wrap = el('div', {});
  page.appendChild(wrap);
  await draw(wrap);
}

async function draw(wrap) {
  // Пазим докъде е превъртяно (и страницата, и широката матрица) — иначе
  // всеки запис на клетка връща потребителя най-горе. (Похватът е от blanka.js.)
  const prevBox = wrap.querySelector('.fees-scrollbox');
  const pageEl = $('#page');
  const keep = {
    x: prevBox ? prevBox.scrollLeft : 0,
    page: pageEl ? pageEl.scrollTop : 0,
    win: window.scrollY || 0,
  };

  clear(wrap);
  wrap.appendChild(topBar(wrap));
  const host = el('div', {});
  wrap.appendChild(host);
  host.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  if (_tab === 'analytics') await drawAnalytics(host, keep, wrap);
  else await drawMatrix(host, wrap, keep);
}

// Превъртането на широките таблици се връща там, където беше — и в матрицата,
// и в справката по счетоводители (и двете са в .fees-scrollbox).
function restoreScroll(host, keep) {
  const box = host.querySelector('.fees-scrollbox');
  if (box) box.scrollLeft = keep.x;
  const pageEl = $('#page');
  if (pageEl) pageEl.scrollTop = keep.page;
  try { if (keep.win) window.scrollTo(0, keep.win); } catch (e) {}
}

function topBar(wrap) {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= now - 3; y--) years.push(y);
  if (!years.includes(_year)) years.unshift(_year);
  const yearSel = el('select', { onChange: (e) => { _year = Number(e.target.value); draw(wrap); } },
    ...years.map((y) => el('option', { value: y, text: String(y), selected: y === _year })));

  return el('div', { class: 'filter-bar' },
    tabBar([{ id: 'matrix', label: 'Такси' }, { id: 'analytics', label: 'Анализи' }],
      _tab, (id) => { _tab = id; draw(wrap); }, 'Такси или анализи', { inline: true }),
    field('Година', yearSel, { size: 's' }));
}

// ---- Месечна матрица + годишно приключване --------------------------------

async function drawMatrix(host, wrap, keep) {
  let data;
  try { data = await api('/fees?year=' + _year); }
  catch (e) { clear(host); host.appendChild(el('div', { class: 'cal-hint', text: e.message })); return; }
  clear(host);

  const clients = Array.isArray(data.clients) ? data.clients : [];
  // Таблото в „Анализи" брои клиентите на всеки счетоводител от ТОЗИ списък —
  // затова се запомня още тук, вместо после да се пита сървърът пак.
  rememberClients(_year, clients);
  const monthly = clients.filter((c) => c.kind !== 'annual');
  const annual = clients.filter((c) => c.kind === 'annual');
  const totals = data.totals || {};
  // Докъде е начислено: 7 = „до юли включително". 0 (бъдеща година) и липсващо
  // стават null — тогава колоната за дълга не си измисля месец.
  const through = monthOrNull(data.through_month);

  // Обобщението горе. „Общ дълг" показва САМО реално дължимото
  // (receivable_cents), а не нетното: надплатилият е задължение КЪМ клиента и
  // не бива да маскира чуждия дълг — 1000 дължими и 1000 надплатени не са
  // „никой нищо не дължи". Надплатеното стои отделно, за да не изчезне.
  // При стар сървър без полето се пада към нетното, както беше.
  const receivable = totals.receivable_cents != null ? Number(totals.receivable_cents) : Number(totals.debt_cents);
  const overpaid = Number(totals.overpaid_cents) || 0;
  const debtCard = statCard('Общ дълг', eur(receivable), 'за ' + _year + ' г.' + (through ? ' (до ' + MONTHS_FULL[through - 1] + ')' : ''),
    { warn: receivable > 0, subAlarm: receivable > 0 });
  const paidCard = statCard('Общо платено', eur(totals.paid_cents), 'за ' + _year + ' г.');
  const cards = [debtCard, paidCard];
  // Картата за надплатеното се показва само когато има такова — иначе е шум.
  if (overpaid > 0) {
    cards.push(statCard('Надплатено от клиенти', eur(overpaid),
      'не намалява дълга горе'));
  }
  host.appendChild(el('div', { class: 'cards' }, ...cards));

  // Дълговите клетки и корекциите се обновяват НА МЯСТО след запис
  // (виж refreshNumbers).
  const debtCells = new Map();
  const adjCells = new Map();
  // Числата от последната заявка, редовете, които СЕГА са на екрана, и редът
  // със сборовете на текущата таблица. Държат се тук, защото филтърът
  // пречертава само таблицата, а печатът и опресняването питат „кое се вижда".
  let lastData = data;
  let shown = [];
  let sums = null;

  // Дългът се смята на СЪРВЪРА (пренос, корекции, ДДС месеци). Затова след
  // запис не преначертаваме екрана — само дърпаме числата и ги подменяме:
  // пълното пречертаване би откраднало фокуса от следващата клетка.
  // Връща прясното състояние — дневникът на корекциите стъпва на него.
  async function refreshNumbers() {
    let fresh;
    try { fresh = await api('/fees?year=' + _year); } catch (e) { return null; }
    lastData = fresh;
    (fresh.clients || []).forEach((c) => {
      const td = debtCells.get(c.id);
      if (td) paintDebt(td, c.debt_cents);
      const adj = adjCells.get(c.id);
      if (adj) paintAdj(adj.btn, c, adj.name);
    });
    // Редовете НАРОЧНО не се пресяват наново тук: при филтър „само дължащи"
    // клиентът щеше да изчезне изпод пръстите на човека в мига, в който
    // плащането го изравни — и следващата клетка да остане без фокус.
    // Обновяват се само ЧИСЛАТА, включително сборовете за същите редове.
    if (sums) {
      const ids = new Set(shown.map((r) => Number(r.id)));
      shown = monthlyOf(fresh).filter((c) => ids.has(Number(c.id)));
      sums.paint(fresh, filterActive() ? shown : null);
    }
    const t = fresh.totals || {};
    const setVal = (card, v) => { const n = card.querySelector('.value'); if (n) n.textContent = eur(v); };
    setVal(debtCard, t.debt_cents);
    setVal(paidCard, t.paid_cents);
    return fresh;
  }

  const savePayment = (clientId, month, cents) => api('/fees/payments', {
    method: 'PUT',
    body: JSON.stringify({ fee_client_id: clientId, year: _year, month, amount_cents: cents, note: '' }),
  }).then(refreshNumbers);

  // ---- Месечните ----
  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Месечни такси — ' + _year }),
    el('span', { style: 'color:var(--muted);font-size:13px;margin-left:auto', text: monthly.length + ' клиента' }),
    el('button', { class: 'btn sm', type: 'button', text: '+ Клиент', onClick: () => clientForm(wrap, null) }),
    // Свиване на широката матрица: кои колони да се виждат (изборът се помни).
    monthly.length ? el('button', {
      class: 'btn sm ghost fees-cols-open', type: 'button', text: '▦ Колони',
      title: 'Изберете кои колони да се виждат',
      'aria-label': 'Колони — избор кои колони да се виждат в таблицата',
      onClick: () => columnsDialog(wrap),
    }) : null,
    // Печата се това, което е НА ЕКРАНА (текущата година, текущите числа,
    // текущите колони И текущият филтър) — затова моделът се сглобява от вече
    // заредените данни, без нова заявка.
    monthly.length ? el('button', {
      class: 'btn sm ghost', type: 'button', text: '🖨 Печат',
      title: 'Печат на таблицата за ' + _year + ' г.',
      'aria-label': 'Печат на таблицата за ' + _year + ' г.',
      onClick: doPrint,
    }) : null,
    // Excel-ът повтаря ЕКРАНА, както и печатът: същите редове (филтърът) и
    // същите колони (изборът) — иначе файлът е втора, различна справка.
    monthly.length ? el('button', {
      class: 'btn sm ghost fees-xlsx', type: 'button', text: '⤓ Excel',
      title: 'Сваля таблицата като Excel — с текущия филтър и видимите колони.',
      'aria-label': 'Excel — сваля таблицата за ' + _year + ' г.',
      onClick: doXlsx,
    }) : null,
    el('button', { class: 'btn sm ghost', type: 'button', text: '⤓ Внос от файл', onClick: () => importForm(wrap) })));

  // Филтрите са над МЕСЕЧНАТА матрица. Без тази бележка пълната годишна таблица
  // под филтриран изглед изглежда като бъг („защо тия не се филтрират"). Стои
  // тук, а не в годишния панел, защото се показва и скрива заедно с филтъра.
  const annualNote = el('div', {
    class: 'cal-hint fees-annual-nofilter', style: 'display:none',
    text: 'Филтрите горе важат за месечната таблица. Годишните приключвания се показват всички.',
  });

  // Хартията повтаря ЕКРАНА: филтрираният изглед се печата филтриран, а празен
  // резултат не бива да излиза като лист с нула реда.
  function doPrint() {
    if (!shown.length) { toast('Няма какво да се отпечата — филтърът не показва нито един клиент.', true); return; }
    const all = monthlyOf(lastData);
    printFeesTable(printModel(lastData, through, shown,
      filterActive() ? { shown: shown.length, total: all.length } : null));
  }

  // Същото правило като печата: сваля се това, което е НА ЕКРАНА. Празен
  // резултат не бива да става файл с нула реда — човек го отваря и решава, че
  // данните са изчезнали.
  function doXlsx() {
    if (!shown.length) { toast('Няма какво да се свали — филтърът не показва нито един клиент.', true); return; }
    exportFeesMatrix(xlsxMatrixModel(through, shown));
  }

  if (!monthly.length) {
    panel.appendChild(emptyState('€', 'Още няма месечни клиенти за ' + _year + ' г.', '+ Клиент', () => clientForm(wrap, null)));
  } else {
    const box = el('div', { class: 'fees-scrollbox', style: 'overflow-x:auto' });
    // Бележката „сборовете са само за филтрираните" стои ВИДИМО до тях: иначе
    // едно и също число значи ту „за всички", ту „за показаните".
    const note = el('div', { class: 'cal-hint fees-sum-note', style: 'display:none' });
    // Лентата се строи ВЕДНЪЖ и после само таблицата под нея се пречертава —
    // иначе всяка буква в търсачката би отнемала фокуса от полето.
    const bar = filtersBar(monthly, () => drawTable());
    panel.appendChild(bar.node);
    // Легендата стои НАД таблицата, до филтрите: под нея са 91 реда и никой
    // не стига дотам, за да разбере какво значи ✻ или · (В.1).
    panel.appendChild(el('div', { class: 'cal-hint fees-legend' },
      el('span', { class: 'fees-vat-mark', 'aria-hidden': 'true', text: '✻' }),
      el('span', { text: ' ДДС месец' }),
      el('span', { class: 'fees-legend-sep', 'aria-hidden': 'true', text: ' · ' }),
      el('span', { class: 'fees-na-mark', 'aria-hidden': 'true', text: '·' }),
      el('span', { text: ' извън периода на клиента' })));
    panel.appendChild(box);
    panel.appendChild(note);
    panel.appendChild(el('div', { class: 'cal-hint', text: 'Клетките се попълват направо в таблицата — сумата се записва, щом излезете от полето. Долният ред е сборът за всеки месец по показаните клиенти.' }));

    function paintNote(n, total) {
      annualNote.setAttribute('style', filterActive() && annual.length ? 'padding:2px 0 0' : 'display:none');
      if (!filterActive()) { note.textContent = ''; note.setAttribute('style', 'display:none'); return; }
      note.setAttribute('style', 'padding:2px 0 0');
      note.textContent = n
        ? 'Филтър: показани ' + n + ' от ' + total + ' клиента. Сборовете отдолу са само за ФИЛТРИРАНИТЕ редове; картите горе остават за цялата година.'
        : 'Филтър: няма нито един от ' + total + ' клиента по това търсене.';
    }

    // Пречертава САМО таблицата (не целия екран): филтрите горе си остават на
    // място заедно с фокуса и написаното в тях.
    function drawTable() {
      clear(box);
      debtCells.clear();
      adjCells.clear();
      const all = monthlyOf(lastData);
      shown = all.filter(matchesFilters);
      bar.setCount(shown.length, all.length);
      if (!shown.length) {
        // Празна таблица изглежда като повреда. Казваме какво е станало и даваме
        // изхода на един клик — иначе човек остава заклещен в празен екран.
        sums = null;
        paintNote(0, all.length);
        box.appendChild(emptyState('⌕',
          'Няма клиент по тези филтри. Опитайте с друго име или изчистете филтрите.',
          'Изчисти филтрите', bar.clearAll));
        return;
      }
      // Главата се сглобява от СЪЩИТЕ проверки като редовете и сборовете
      // (colOn/visibleMonths) — иначе трите се разминават.
      const head = el('tr', {},
        el('th', { scope: 'col', style: TH, text: 'Клиент' }),
        colOn('signature') ? el('th', { scope: 'col', style: TH, text: 'Подпис' }) : null,
        colOn('accountant') ? el('th', { scope: 'col', style: TH, text: 'Работи го' }) : null,
        ...(colOn('fees') ? [
          el('th', { scope: 'col', style: TH, title: 'Основна месечна такса (в стария ексел: D)' },
            el('span', { text: 'Основна такса' }), el('span', { class: 'fees-old-code', text: ' (D)' })),
          el('th', { scope: 'col', style: TH, title: 'Такса за месеците с активна ДДС дейност (в стария ексел: E)' },
            el('span', { text: 'ДДС такса' }), el('span', { class: 'fees-old-code', text: ' (E)' })),
        ] : []),
        colOn('carry') ? el('th', { scope: 'col', style: TH, title: 'Пренос от минала година', text: 'Пренос' }) : null,
        ...visibleMonths().map((m) => el('th', {
          scope: 'col', style: TH + ';text-align:center', title: MONTHS_FULL[m - 1], text: MONTHS_SHORT[m - 1],
        })),
        colOn('adj') ? el('th', { scope: 'col', style: TH, title: 'Еднократни корекции по договорка', text: 'Корекции' }) : null,
        debtHead(through));
      const tb = el('tbody');
      shown.forEach((c) => tb.appendChild(monthlyRow(c, { wrap, savePayment, refreshNumbers, debtCells, adjCells })));
      sums = sumsFoot();
      box.appendChild(el('table', { class: 'fees-table' }, el('thead', {}, head), tb, sums.tfoot));
      // При активен филтър сборовете се смятат от ВИДИМИТЕ редове: months_totals
      // от сървъра е за всички клиенти и подвалът щеше да противоречи на редовете.
      sums.paint(lastData, filterActive() ? shown : null);
      paintNote(shown.length, all.length);
    }

    drawTable();
  }
  host.appendChild(panel);

  // ---- Годишно приключване (еднократна такса, извън месечната логика) ----
  const aPanel = el('div', { class: 'panel fees-annual' });
  aPanel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Годишно приключване — ' + _year }),
    el('span', { style: 'color:var(--muted);font-size:13px;margin-left:auto', text: annual.length + ' клиента' })));
  if (annual.length) aPanel.appendChild(annualNote);
  if (!annual.length) {
    aPanel.appendChild(emptyState('€', 'Няма клиенти с годишна такса за ' + _year + ' г.'));
  } else {
    const tb = el('tbody');
    annual.forEach((c) => tb.appendChild(annualRow(c, { wrap, savePayment, debtCells })));
    // Годишната таблица уважава СЪЩИЯ избор: скрил ли е човек „Подпис", той не
    // бива да се появява две педи по-долу.
    aPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Клиент' }),
      colOn('signature') ? el('th', { scope: 'col', text: 'Подпис' }) : null,
      colOn('accountant') ? el('th', { scope: 'col', text: 'Работи го' }) : null,
      el('th', { scope: 'col', text: 'Такса' }),
      el('th', { scope: 'col', text: 'Платено' }),
      el('th', { scope: 'col', text: 'Остава' }))), tb));
    aPanel.appendChild(el('div', { class: 'cal-hint', text: 'Годишното приключване е еднократна такса — не влиза в месечната сметка за дълг.' }));
  }
  host.appendChild(aPanel);

  // Връщаме превъртането там, където беше преди преначертаването.
  restoreScroll(host, keep);
}

// ---- Лентата с филтри ------------------------------------------------------
//
// Всяко поле минава през field() (правилото за достъпност в целия проект):
// падащо меню без свързан надпис се чете от екранния четец като „списък" — без
// да казва списък за какво. Броячът „N от 91" стои ДО филтрите, защото първият
// въпрос след всеки филтър е „колко останаха".
function filtersBar(monthly, onChange) {
  const count = el('span', {
    class: 'fees-filter-count',
    style: 'color:var(--muted);font-size:13px;white-space:nowrap;margin-left:auto',
  });
  const clearBtn = el('button', {
    class: 'btn sm ghost fees-filter-clear', type: 'button', text: '× Изчисти филтрите',
    title: 'Маха всички филтри и показва всички клиенти',
    style: 'display:none', onClick: () => clearAll(),
  });

  const commit = () => { saveFilters(); syncClear(); onChange(); };

  const sigSel = el('select', {
    class: 'fees-filter-signature',
    onChange: (e) => { _filters.sig = e.target.value; commit(); },
  },
    el('option', { value: '', text: 'Всички', selected: !_filters.sig }),
    ...SIGNATURES.map((sg) => el('option', { value: sg.code, text: sigLabel(sg.code), selected: _filters.sig === sg.code })));

  // Счетоводителите идват ОТ ДАННИТЕ, не от твърд списък: нает утре човек се
  // появява сам, а напуснал изчезва, без да се пипа код.
  const names = [];
  monthly.forEach((c) => {
    const n = String(c.accountant_name || '').trim();
    if (n && names.indexOf(n) < 0) names.push(n);
  });
  // Запомненият счетоводител може да го няма в ТАЗИ година (нов клиентски
  // списък, сменена година). Тогава опцията пак се добавя: иначе менюто щеше да
  // показва „Всички", докато филтърът реже редовете — точно разсинхронът, който
  // кара човек да реши, че екранът е счупен.
  if (_filters.acc && _filters.acc !== NO_ACCOUNTANT && names.indexOf(_filters.acc) < 0) names.push(_filters.acc);
  names.sort((a, b) => a.localeCompare(b, 'bg'));
  const accSel = el('select', {
    class: 'fees-filter-accountant',
    onChange: (e) => { _filters.acc = e.target.value; commit(); },
  },
    el('option', { value: '', text: 'Всички', selected: !_filters.acc }),
    ...names.map((n) => el('option', { value: n, text: n, selected: _filters.acc === n })),
    // Клиентите без счетоводител са група като всяка друга — иначе те са
    // единствените, които никой филтър не може да покаже.
    el('option', { value: NO_ACCOUNTANT, text: '(без счетоводител)', selected: _filters.acc === NO_ACCOUNTANT }));

  const stateSel = el('select', {
    class: 'fees-filter-state',
    onChange: (e) => { _filters.state = e.target.value; commit(); },
  },
    el('option', { value: 'all', text: 'Всички', selected: _filters.state === 'all' }),
    el('option', { value: 'debt', text: 'Само дължащи', selected: _filters.state === 'debt' }),
    el('option', { value: 'settled', text: 'Само издължени', selected: _filters.state === 'settled' }),
    el('option', { value: 'overpaid', text: 'Надплатили', selected: _filters.state === 'overpaid' }));

  // ЕДИННАТА търсачка на приложението (лупа, „×", изчакване). value: _filters.q
  // се пази между отварянията — полето трябва да го отразява, иначе филтърът
  // действа, а полето изглежда празно (същият урок като в бланката).
  const search = searchInput({
    placeholder: 'Търси клиент…', ariaLabel: 'Търсене на клиент по име',
    value: _filters.q, onSearch: (v) => { _filters.q = v; commit(); },
  });

  function clearAll() {
    _filters = defaultFilters();
    saveFilters();
    sigSel.value = '';
    accSel.value = '';
    stateSel.value = 'all';
    if (search.inputEl) { search.inputEl.value = ''; search.classList.remove('has-value'); }
    syncClear();
    onChange();
    // Бутонът, който току-що беше натиснат, изчезва (няма какво да се чисти) —
    // фокусът щеше да падне на <body>. Връщаме го на търсачката, която е и
    // следващото естествено действие. (Същият похват като при „Колони".)
    if (search.inputEl) search.inputEl.focus();
  }

  // Бутонът за изчистване се показва само когато има какво да се чисти —
  // иначе е постоянен шум над таблицата.
  function syncClear() { clearBtn.setAttribute('style', filterActive() ? '' : 'display:none'); }
  syncClear();

  const node = el('div', { class: 'filter-bar fees-filter-bar' },
    search,
    field('Подпис', sigSel, { size: 'm' }),
    field('Счетоводител', accSel, { size: 'm' }),
    field('Състояние', stateSel, { size: 'm' }),
    count, clearBtn);

  return {
    node,
    clearAll,
    setCount: (n, total) => {
      count.textContent = 'Показани: ' + n + ' от ' + total + ' клиента';
      count.setAttribute('title', filterActive()
        ? 'Филтърът показва ' + n + ' от общо ' + total + ' месечни клиенти.'
        : 'Всички ' + total + ' месечни клиенти се виждат.');
    },
  };
}

// ---- Заглавие на колоната „Дълг" ------------------------------------------
//
// ЗАЩО: „Дълг" без момент се чете като „дълг за цялата година" и всяка есен
// някой пита защо числото е по-малко от очакваното. through_month казва докъде
// е начислено (7 = до юли включително) — и точно това пише на заглавието.
function debtHead(through) {
  const m = monthOrNull(through);
  return el('th', {
    scope: 'col', style: TH + ';text-align:right',
    title: m
      ? 'Дългът е изчислен към края на ' + MONTHS_FULL[m - 1] + ' ' + _year + ' г. — следващите месеци още не се начисляват.'
      : 'Дългът е към момента — за ' + _year + ' г. още нищо не е начислено.',
  },
    el('div', { text: m ? 'Дълг към ' + MONTHS_FULL[m - 1] : 'Дълг' }),
    el('div', { style: 'font-weight:400;font-size:11px;color:var(--muted)', text: 'сума към момента' }));
}

// ---- Сборове под месеците --------------------------------------------------
//
// Сборът за всеки месец. Сървърът го дава готов в months_totals (само за
// месечните клиенти); ако полето още липсва — например екранът е качен преди
// сървъра — сумираме клетките, които и без това стоят на екрана. Това НЕ е
// втора сметка за дълга: същите числа, само събрани по колона.
function monthsTotals(data) {
  const given = Array.isArray(data && data.months_totals) ? data.months_totals : null;
  if (!given) return monthsTotalsOfRows(monthlyOf(data));
  const out = [];
  for (let m = 1; m <= 12; m++) out.push({ month: m, paid_cents: 0, expected_cents: 0 });
  given.forEach((r) => {
    const m = monthOrNull(r && r.month);
    if (!m) return;
    out[m - 1].paid_cents = Number(r.paid_cents) || 0;
    out[m - 1].expected_cents = Number(r.expected_cents) || 0;
  });
  return out;
}

// Сборовете за ДАДЕНИ редове. Ползва се при активен филтър (и когато сървърът
// още не дава months_totals). ЗАЩО е нужно: months_totals е за ВСИЧКИ клиенти —
// при филтър подвалът щеше да показва едно, а редовете над него друго, и никой
// не може да познае кое число какво значи.
function monthsTotalsOfRows(rows) {
  const out = [];
  for (let m = 1; m <= 12; m++) out.push({ month: m, paid_cents: 0, expected_cents: 0 });
  (rows || []).forEach((c) => {
    (c.months || []).forEach((x) => {
      const m = monthOrNull(x && x.month);
      if (!m) return;
      out[m - 1].paid_cents += Number(x.paid_cents) || 0;
      out[m - 1].expected_cents += Number(x.expected_cents) || 0;
    });
  });
  return out;
}

// Два РЕДА, а не две числа в една клетка: при дванайсет колони окото сканира
// хоризонтално („колко влезе всеки месец"), а „1200 / 1500" в клетка се чете
// като дроб и се губи. Начисленото стои отдолу, за да си личи откъде до къде.
function sumsFoot() {
  // Сборовете се правят САМО за видимите месеци и стъпват на видимите водещи
  // колони. Ако това се разсинхронизира с главата, числата увисват под чужди
  // месеци — най-опасната възможна грешка на този екран.
  const months = visibleMonths();
  const paid = [], expected = [];
  // Надписите се пипат при всяко пребоядисване: при филтър те КАЗВАТ, че сборът
  // е само за показаните редове (символи и цвят не стигат — числото трябва да
  // се назове).
  const heads = [];
  const row = (cls, label, hint, cells) => {
    const tr = el('tr', { class: 'fees-sum ' + cls });
    const th = el('th', {
      scope: 'row', colspan: String(leadSpan()), title: hint,
      style: TD + ';text-align:right;font-weight:700', text: label,
    });
    heads.push({ th, label, hint });
    tr.appendChild(th);
    months.forEach((m) => {
      const td = el('td', {
        'data-month': String(m),
        style: TD + ';text-align:center;font-weight:700',
      });
      cells.push(td);
      tr.appendChild(td);
    });
    // Корекции + Дълг остават празни: сбор на дълга ТУК би се бъркал с картата
    // „Общ дълг" горе (тя включва и годишните приключвания).
    tr.appendChild(el('td', { colspan: String(tailSpan()) }));
    return tr;
  };
  const tfoot = el('tfoot', { style: 'border-top:2px solid var(--border)' },
    row('fees-sum-paid', 'Платено по месеци',
      'Сборът на платеното за месеца по всички месечни клиенти.', paid),
    row('fees-sum-expected', 'Начислено по месеци',
      'Колко е начислено за месеца по всички месечни клиенти.', expected));

  // rows === null → числата на СЪРВЪРА (за всички клиенти).
  // rows === списък → сборове САМО за тези редове (активен филтър).
  const paint = (data, rows) => {
    const filtered = Array.isArray(rows);
    const all = filtered ? monthsTotalsOfRows(rows) : monthsTotals(data);
    heads.forEach((h) => {
      h.th.textContent = h.label + (filtered ? ' (филтрирани)' : '');
      h.th.setAttribute('title', h.hint + (filtered ? ' Смятан САМО по показаните редове.' : ''));
    });
    months.forEach((m, i) => {
      const t = all[m - 1];
      // Нулата тук е ЧИСЛО, не празно поле: празна клетка в ред със сборове
      // се чете като „няма данни", а тя значи „нищо не е влязло".
      paid[i].textContent = eur(t.paid_cents);
      expected[i].textContent = eur(t.expected_cents);
      paid[i].setAttribute('title', MONTHS_FULL[m - 1] + ': платено ' + eur(t.paid_cents)
        + ' от начислени ' + eur(t.expected_cents) + '.');
    });
  };
  return { tfoot, paint };
}

// ---- Печат -----------------------------------------------------------------
//
// Моделът за печат се сглобява ТУК, вече форматиран: преобразуването
// „центове ↔ пари" остава едно (eur/fromCents), а print.js е чист изглед и
// няма как да покаже число, различно от екрана. На хартията влиза само това,
// което е колона в таблицата — бележките по клиента остават на екрана.
// rows — редовете, които са НА ЕКРАНА (при активен филтър са по-малко).
// filtered — { shown, total } или null; влиза на листа, за да не се сбърка
// филтриран изглед с пълна справка, когато хартията тръгне по хората.
// ЗАЩО дългът се дели на ДВЕ: собственикът печата листа „Задължения", а
// надплатилият излизаше на него с ОТРИЦАТЕЛНО число в колона „Дълг" — тоест
// като дълг. Правилото е същото като на екрана: ДЪЛЖИМОТО и НАДПЛАТЕНОТО са
// две различни числа и не се изваждат едно от друго. 1000 дължими и 1000
// надплатени не са „никой нищо не дължи".
function printModel(data, through, rows, filtered) {
  const monthly = Array.isArray(rows) ? rows : monthlyOf(data);
  const t = filtered ? monthsTotalsOfRows(monthly) : monthsTotals(data);
  const m = monthOrNull(through);

  // Сборовете за дълга се смятат от ОТПЕЧАТАНИТЕ редове — само така подвалът
  // може да съвпадне с колоната над него. (Числата на сървъра в totals покриват
  // и ГОДИШНИТЕ приключвания, които в тази таблица ги няма — затова те влизат
  // отделно, като бележка под листа, вместо да разминат сбора с колоната.)
  let receivable = 0;
  let overpaid = 0;
  monthly.forEach((c) => {
    const d = Number(c.debt_cents) || 0;
    if (d > 0) receivable += d;
    else if (d < 0) overpaid += -d;
  });
  const tot = (data && data.totals) || {};
  const yearRec = tot.receivable_cents != null ? Number(tot.receivable_cents) : null;
  const yearOver = Number(tot.overpaid_cents) || 0;
  // Целогодишните числа на сървъра влизат САМО когато казват нещо различно от
  // отпечатаното (тоест има и годишни приключвания) и изгледът НЕ е филтриран.
  const whole = (!filtered && yearRec != null && (yearRec !== receivable || yearOver !== overpaid))
    ? { receivable: eur(yearRec), overpaid: eur(yearOver) }
    : null;
  // Печата се това, което се ВИЖДА — включително скритите колони. Заглавията и
  // стойностите пътуват като ЕДИН списък (leadHeads ↔ row.lead), за да няма как
  // редът да се разбие спрямо главата. Същият урок като colspan-а в tfoot.
  const months = visibleMonths();
  const leadHeads = ['Клиент'];
  if (colOn('signature')) leadHeads.push('Подпис');
  if (colOn('accountant')) leadHeads.push('Счетоводител');
  return {
    year: _year,
    through: m ? MONTHS_FULL[m - 1] : '',
    filtered: filtered || null,
    monthLabels: months.map((x) => MONTHS_SHORT[x - 1]),
    leadHeads,
    rows: monthly.map((c) => {
      const lead = [c.label || ('№ ' + c.id)];
      if (colOn('signature')) lead.push(c.signature || '');
      if (colOn('accountant')) lead.push(c.accountant_name || '');
      const d = Number(c.debt_cents) || 0;
      return {
        lead,
        months: months.map((x) => {
          const info = (c.months || []).find((z) => Number(z.month) === x) || {};
          const cents = Number(info.paid_cents) || 0;
          // Същите знаци като на екрана: · = месец извън периода, ✻ = ДДС такса.
          if (!monthApplies(c, x) && !cents) return { text: '·', mark: '' };
          return { text: fromCents(cents), mark: info.vat_active ? '✻' : '' };
        }),
        // Надплатилият излиза с ДУМА и положително число („120,00 € аванс"), не
        // с „−120,00 €": на черно-бял лист цвят няма, а минусът в колона „Дълг"
        // се чете като дълг.
        debt: d < 0 ? eur(-d) + ' аванс' : eur(d),
        owes: d > 0,
        over: d < 0,
      };
    }),
    sums: {
      // На хартия колоните са тесни — сумите вървят без знака за валута, а
      // „суми в евро" пише веднъж в заглавието.
      paid: months.map((x) => fromCents(t[x - 1].paid_cents) || '0,00'),
      expected: months.map((x) => fromCents(t[x - 1].expected_cents) || '0,00'),
      // Дългът: САМО положителните. null при надплатеното значи „няма такива" и
      // редът изобщо не се печата — иначе „0,00 €" изглежда като данни.
      receivable: eur(receivable),
      overpaid: overpaid > 0 ? eur(overpaid) : null,
      whole,
    },
  };
}

// ---- Модел за Excel --------------------------------------------------------
//
// ЗАЩО е тук, а не в xlsx.js: кои редове се виждат (филтърът) и кои колони
// (изборът) знае САМО екранът — точно както при печата. Затова оттук излиза
// готов списък, а xlsx.js решава единствено как става колона.
//
// Разликата с printModel: тук сумите пътуват като ЦЕНТОВЕ, не форматирани.
// В Excel клетката трябва да е ЧИСЛО, иначе колоната не се сумира — а
// превръщането център → число е едно, в xlsx.js (eurNum).
function xlsxMatrixModel(through, rows) {
  const months = visibleMonths();
  const m = monthOrNull(through);
  return {
    year: _year,
    debtHead: m ? 'Дълг към ' + MONTHS_FULL[m - 1] : 'Дълг',
    months: months.map((x) => ({ month: x, label: MONTHS_SHORT[x - 1] })),
    cols: {
      signature: colOn('signature'), accountant: colOn('accountant'),
      fees: colOn('fees'), carry: colOn('carry'), adj: colOn('adj'),
    },
    rows: (rows || []).map((c) => ({
      label: c.label || ('№ ' + c.id),
      signature: c.signature || '',
      accountant: c.accountant_name || '',
      fee_base_cents: c.fee_base_cents,
      fee_active_cents: c.fee_active_cents,
      carryover_cents: c.carryover_cents,
      // Нула, а не празно: празната клетка в Excel не се сумира и колоната
      // започва да лъже. Знакът „·" за месец извън периода е за ЕКРАНА.
      months: months.map((x) => {
        const info = (c.months || []).find((z) => Number(z.month) === x) || {};
        return Number(info.paid_cents) || 0;
      }),
      adjustments_cents: c.adjustments_cents,
      debt_cents: c.debt_cents,
    })),
  };
}

// ---- Диалог „Колони" -------------------------------------------------------
//
// Работи върху КОПИЕ на избора: „Отказ" не бива да е променил нищо. Всеки
// превключвател е истински checkbox, свързан с надписа си през field() —
// правилото за достъпност в целия проект. „Клиент" и „Дълг" също са в списъка,
// но заключени: така се вижда ЗАЩО ги няма, вместо да изглежда като пропуск.
function columnsDialog(wrap) {
  const draft = { months: _cols.months.slice() };
  COL_GROUPS.forEach((g) => { draft[g.id] = colOn(g.id); });

  const boxes = new Map();
  const monthBoxes = [];
  const allMonths = input({ type: 'checkbox', 'data-col': 'months-all' });

  // Редът е „отметка + надпис" (обърнат field()), а не надпис НАД полето:
  // списъкът е осемнайсет реда. Връзката label[for] ↔ input остава — тя е
  // достъпното име на превключвателя.
  const toggleRow = (label, box) => {
    const f = field(label, box);
    f.setAttribute('style', 'display:flex;flex-direction:row-reverse;justify-content:flex-end;'
      + 'align-items:center;gap:8px;margin:0;padding:3px 0');
    return f;
  };

  // Едно място, което връща видимото състояние на всички отметки — иначе
  // груповият превключвател и дванайсетте месеца се разминават.
  const sync = () => {
    COL_GROUPS.forEach((g) => { boxes.get(g.id).checked = draft[g.id]; });
    monthBoxes.forEach((b, i) => { b.checked = draft.months[i]; });
    const on = draft.months.filter(Boolean).length;
    allMonths.checked = on === 12;
    // Частичен избор: нито включено, нито изключено — иначе отметката лъже.
    allMonths.indeterminate = on > 0 && on < 12;
  };

  const groupRows = COL_GROUPS.map((g) => {
    const box = input({ type: 'checkbox', 'data-col': g.id });
    box.addEventListener('change', () => { draft[g.id] = box.checked; });
    boxes.set(g.id, box);
    return toggleRow(g.label, box);
  });

  allMonths.addEventListener('change', () => {
    for (let i = 0; i < 12; i++) draft.months[i] = allMonths.checked;
    sync();
  });

  const monthRows = MONTHS_FULL.map((label, i) => {
    const box = input({ type: 'checkbox', 'data-col': 'month', 'data-month': String(i + 1) });
    box.addEventListener('change', () => { draft.months[i] = box.checked; sync(); });
    monthBoxes.push(box);
    return toggleRow(label, box);
  });

  const lockedRow = (id, label) => toggleRow(label, input({
    type: 'checkbox', 'data-col': id, checked: true, disabled: true,
    title: 'Тази колона не се скрива.',
  }));

  const grid = (...kids) => el('div', {
    style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:0 16px',
  }, ...kids);
  const cap = (text) => el('div', { style: 'font-weight:700;margin:10px 0 4px', text });

  sync();

  const showAll = () => {
    draft.months = new Array(12).fill(true);
    COL_GROUPS.forEach((g) => { draft[g.id] = true; });
    sync();
  };

  // Прилага се при „Готово": докато се цъка, таблицата отдолу не подскача.
  const apply = async () => {
    _cols = { months: draft.months.slice() };
    COL_GROUPS.forEach((g) => { _cols[g.id] = draft[g.id]; });
    saveCols();
    closeModal();
    await draw(wrap);
    // Прерисуването пресъздава бутона и фокусът пада на <body> — човек на
    // клавиатура остава в нищото. Връщаме го на „Колони" (същият похват като
    // _refocusHamburger в src/shell/shell.js).
    const again = wrap.querySelector('.fees-cols-open');
    if (again) again.focus();
  };

  // openModal иска ОБЕКТ ({ title, body, foot }) — с два аргумента диалогът
  // мълчи. Този капан вече се е случвал в този файл.
  openModal({
    title: 'Кои колони да се виждат', wide: true,
    body: el('div', {},
      el('p', {
        style: 'margin:0 0 6px;color:var(--muted);font-size:13px',
        text: 'Изключената колона изчезва от таблицата, от сборовете отдолу и от печата. Изборът се помни за следващия път.',
      }),
      cap('Колони'),
      grid(...groupRows),
      cap('Месеци'),
      toggleRow('Всички месеци', allMonths),
      grid(...monthRows),
      cap('Винаги видими'),
      lockedRow('client', 'Клиент'),
      lockedRow('debt', 'Дълг'),
      el('div', {
        class: 'cal-hint', style: 'padding:2px 0 0',
        text: '„Клиент" и „Дълг" не се скриват — без тях таблицата не отговаря на въпроса кой колко дължи.',
      })),
    foot: el('div', { style: 'display:flex;gap:8px;width:100%' },
      el('button', {
        class: 'btn sm ghost', type: 'button', text: 'Покажи всички колони',
        style: 'margin-right:auto', onClick: showAll,
      }),
      el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: apply })),
  });
}

// Редакция на сума в поле: записва се при ИЗЛИЗАНЕ от полето (change), не на
// всеки знак — иначе „12" тръгва като плащане, докато човек още пише „120".
// (Б.8) Матрицата е 91 клиента × 12 месеца и undo НЯМА. Голото „Записано."
// не казва КЪДЕ е отишла сумата, а сгрешен ред/колона се забелязва чак при
// сверка. Затова: съобщението носи име и месец, клетката просветва, а
// изчистването се обявява като ПРЕМАХВАНЕ (с предишната сума), не като тих
// запис на нула. opts.describe е краткото име на клетката („Аско 2 — май").
function bindAmount(inp, save, opts) {
  const describe = (opts && opts.describe) || '';
  inp.dataset.orig = inp.value;
  inp.addEventListener('change', async () => {
    if (inp.value === inp.dataset.orig) return; // непипнато поле не праща нищо
    const cents = toCents(inp.value);
    if (cents === null) {
      toast('Въведете сума с цифри, например 120 или 120,50.', true);
      inp.value = inp.dataset.orig;
      return;
    }
    const prevCents = toCents(inp.dataset.orig) || 0;
    inp.disabled = true;
    try {
      await save(cents);
      inp.dataset.orig = fromCents(cents);
      inp.value = inp.dataset.orig;
      // Изчистена клетка: казваме какво е ИЗЧЕЗНАЛО и колко е било — иначе
      // случайно изтрита сума минава за „записано" и се губи безшумно.
      if (cents === 0 && prevCents !== 0) {
        toast(describe ? ('Плащането ' + describe + ' е премахнато (беше ' + eur(prevCents) + ').') : ('Плащането е премахнато (беше ' + eur(prevCents) + ').'), true);
      } else {
        toast(describe ? ('Записано: ' + describe + ': ' + eur(cents)) : ('Записано: ' + eur(cents)));
      }
      flashCell(inp);
    } catch (e) {
      // Провалът връща старата стойност: полето не бива да показва сума,
      // която сървърът не е приел.
      toast(e.message, true);
      inp.value = inp.dataset.orig;
    } finally {
      inp.disabled = false;
    }
  });
  // Enter записва (излиза от полето) — без него човек натиска Enter и „нищо
  // не става", докато не кликне другаде.
  inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') inp.blur(); });
  // Открояване на реда и цялата колона, докато полето е на фокус: в матрица
  // от 12 колони окото лесно се подхлъзва един ред по-горе.
  inp.addEventListener('focus', () => highlightCross(inp, true));
  inp.addEventListener('blur', () => highlightCross(inp, false));
  return inp;
}

// Зелено просветване на току-що записаната клетка (класът пада сам).
function flashCell(inp) {
  const td = inp.closest ? inp.closest('td') : null;
  if (!td) return;
  td.classList.remove('fees-flash');
  // Рестартиране на анимацията при бърза поредица от записи в една клетка.
  void td.offsetWidth;
  td.classList.add('fees-flash');
  setTimeout(() => td.classList.remove('fees-flash'), 1200);
}

// Кръстът ред+колона. Колоната се хваща по data-month в СЪЩАТА таблица.
function highlightCross(inp, on) {
  const td = inp.closest ? inp.closest('td') : null;
  if (!td) return;
  const tr = td.closest('tr');
  if (tr) tr.classList.toggle('fees-hl-row', on);
  const month = td.getAttribute('data-month');
  const table = td.closest('table');
  if (!month || !table) { td.classList.toggle('fees-hl-col', on); return; }
  table.querySelectorAll('td[data-month="' + month + '"]').forEach((x) => x.classList.toggle('fees-hl-col', on));
}

// Поле за сума в таблица. Надписът НЕ може да е видим (клетка в матрица),
// затова достъпното име е в aria-label — както в редовете на фактурата.
// Видът е вграден: полетата извън .field нямат кант в общите стилове, а гола
// системна кутийка в матрица от 21 колони изглежда като повреда.
const CELL_INPUT_STYLE = 'padding:4px 5px;border:1px solid var(--border);border-radius:6px;'
  + 'background:var(--surface);color:var(--ink);text-align:right;';
function amountInput(cls, value, ariaLabel, save, width, describe) {
  const inp = input({
    type: 'text', inputmode: 'decimal', class: cls, value,
    'aria-label': ariaLabel,
    style: CELL_INPUT_STYLE + 'width:' + (width || 70) + 'px',
  });
  return bindAmount(inp, save, { describe });
}

// Дългът се рисува на едно място, защото се пипа и при първото рисуване, и
// при опресняването след запис — иначе двете места се разминават.
function paintDebt(td, cents) {
  const owes = Number(cents) > 0;
  td.className = 'fees-debt' + (owes ? ' owes' : '');
  td.setAttribute('style', 'padding:4px 8px;text-align:right;white-space:nowrap;font-weight:700;'
    + (owes ? 'color:var(--red)' : 'color:var(--muted)'));
  td.textContent = eur(cents);
  return td;
}

// Общото правило за таблиците дава 11/18 px отстъп — при 21 колони това е цял
// екран само празнина, затова матрицата се сбива (както бланката).
const TH = 'padding:6px 7px;white-space:nowrap';
const TD = 'padding:4px 7px;white-space:nowrap';

function monthlyRow(c, ctx) {
  const name = c.label || ('№ ' + c.id);
  const tr = el('tr', { 'data-client-id': String(c.id) });

  // Редът НЕ е кликаем през <tr> (правилото на проекта) — редакцията е бутон.
  tr.appendChild(el('td', { class: 'fees-client', style: 'padding:4px 8px;min-width:150px' },
    el('div', { style: 'display:flex;gap:6px;align-items:center' },
      el('strong', { text: name }),
      c.active === false ? el('span', { class: 'tag gray', text: 'неактивен' }) : null,
      el('button', {
        class: 'btn sm ghost', type: 'button', text: '✎',
        title: 'Редакция на клиента', 'aria-label': 'Редакция на „' + name + '"',
        onClick: () => clientForm(ctx.wrap, c),
      })),
    // Периодът се вижда на реда — иначе приглушените клетки изглеждат като бъг.
    periodLabel(c) ? el('div', { class: 'fees-period', style: 'font-size:12px;color:var(--muted)', text: periodLabel(c) }) : null,
    c.note ? el('div', { style: 'font-size:12px;color:var(--muted)', text: c.note }) : null));

  if (colOn('signature')) tr.appendChild(el('td', { style: TD, text: c.signature || '' }));
  if (colOn('accountant')) tr.appendChild(el('td', { style: TD, text: c.accountant_name || '—' }));

  // Таксите D и E се редактират направо на реда — най-честата промяна.
  if (colOn('fees')) {
    const saveClient = (over) => api('/fees/clients/' + c.id, {
      method: 'PUT', body: JSON.stringify(clientPayload(c, over)),
    }).then(ctx.refreshNumbers);
    tr.appendChild(el('td', { style: 'padding:3px 5px' }, amountInput('fees-amount fees-fee-base', fromCents(c.fee_base_cents),
      'Основна такса — ' + name, (cents) => saveClient({ fee_base_cents: cents }))));
    tr.appendChild(el('td', { style: 'padding:3px 5px' }, amountInput('fees-amount fees-fee-active', fromCents(c.fee_active_cents),
      'ДДС такса при активна ДДС дейност — ' + name, (cents) => saveClient({ fee_active_cents: cents }))));
  }

  if (colOn('carry')) tr.appendChild(carryCell(c, ctx));

  visibleMonths().forEach((m) => tr.appendChild(monthCell(c, m, ctx)));

  // Корекциите се ДОБАВЯТ (POST), не се презаписват. Самото число е БУТОН —
  // клик отваря дневника със списъка (дата, защо, колко) и формата за нова.
  if (colOn('adj')) {
    const adjBtn = el('button', {
      class: 'btn sm ghost fees-adj-open', type: 'button',
      onClick: () => adjustmentsDialog(c, ctx),
    });
    paintAdj(adjBtn, c, name);
    // Скрита колона = няма какво да се опреснява (refreshNumbers го проверява).
    ctx.adjCells.set(c.id, { btn: adjBtn, name });
    tr.appendChild(el('td', {
      class: 'fees-adj',
      style: 'padding:3px 5px;white-space:nowrap;text-align:right',
    }, adjBtn));
  }

  // Дългът идва ГОТОВ от сървъра (пренос + корекции + ДДС месеци + периода).
  // Екранът никога не го смята сам — две сметки за едно число се разминават.
  const debtTd = paintDebt(el('td', {}), c.debt_cents);
  ctx.debtCells.set(c.id, debtTd);
  tr.appendChild(debtTd);
  return tr;
}

// ---- Пренос от минала година ----------------------------------------------
//
// ЗАЩО се различават двата вида: сървърът вече пренася сам остатъка от миналата
// година (`carryover_auto: true`). Без знак собственикът не може да разбере дали
// числото е негово, или системата го е сложила — а това мени отговора на „защо
// този клиент изведнъж дължи". Знакът е СИМВОЛ + ДУМИ, не цвят (правилото за
// достъпност). Липсва ли полето изобщо (стар сървър), държим се както досега.
//
// ВНИМАНИЕ (капан): `carryover_auto: true` значи „НЕ е въведен на ръка", а не
// „има пренос" — сметнатата НУЛА също е автоматична. Понеже повечето клиенти
// нямат никакъв пренос, бележка на всеки такъв ред би напълнила таблицата с
// надписи, които не значат нищо. Затова знакът излиза само при пренос, РАЗЛИЧЕН
// от нула; нулевата клетка си остава празна, както досега.
function carryCell(c, ctx) {
  const name = c.label || ('№ ' + c.id);
  const auto = c.carryover_auto === true && Number(c.carryover_cents) !== 0;
  const from = _year - 1;
  const words = 'Автоматичен пренос — изчислен от ' + from + ' г.';
  const td = el('td', {
    class: 'fees-carry-cell', style: 'padding:3px 5px',
    title: auto ? words : null,
  });
  td.appendChild(amountInput('fees-amount fees-carry', fromCents(c.carryover_cents),
    'Пренос от минала година — ' + name + (auto ? '. ' + words : ''),
    (cents) => api('/fees/carryover', {
      method: 'PUT', body: JSON.stringify({ fee_client_id: c.id, year: _year, amount_cents: cents }),
    }).then(ctx.refreshNumbers)));
  if (auto) {
    td.appendChild(el('div', {
      class: 'fees-carry-auto', title: words,
      style: 'font-size:11px;line-height:1.3;color:var(--muted);white-space:nowrap;text-align:right',
    },
      el('span', { 'aria-hidden': 'true', text: '⟳ ' }),
      'от ' + from + ' г.'));
  }
  return td;
}

function monthCell(c, m, ctx) {
  const info = (c.months || []).find((x) => Number(x.month) === m) || {};
  const vat = !!info.vat_active;
  const name = c.label || ('№ ' + c.id);
  const expected = 'Очаквано: ' + eur(info.expected_cents);
  const vatWords = 'Месец с активна ДДС такса (E)';
  const applies = monthApplies(c, m);
  const paid = Number(info.paid_cents) || 0;

  // Месец извън периода на клиента (дошъл през май / спрян през септември) не
  // се начислява. Празна бяла клетка би изглеждала като „неплатено" — затова
  // е приглушена, с точка и с обяснение при посочване/клик (както бланката).
  if (!applies && !paid) {
    const why = periodReason(c);
    return el('td', {
      class: 'fees-cell na', 'data-month': String(m), title: why,
      style: 'padding:4px 6px;text-align:center;color:var(--faint);background:var(--surface-2)',
      // Клик обяснява, вместо да „не се случва нищо".
      onClick: () => toast(why),
    },
      el('span', { class: 'fees-na-mark', 'aria-hidden': 'true', text: '·' }),
      el('span', { class: 'sr-only', text: why }));
  }

  const td = el('td', {
    class: 'fees-cell' + (vat ? ' vat' : '') + (applies ? '' : ' na'),
    'data-month': String(m),
    style: 'padding:4px 6px;text-align:center;white-space:nowrap'
      + (applies ? '' : ';background:var(--surface-2)'),
    title: MONTHS_FULL[m - 1] + ' · ' + expected + (vat ? ' · ' + vatWords : '')
      + (applies ? '' : ' · ' + periodReason(c) + ' Има записано плащане.'),
  });
  td.appendChild(amountInput('fees-amount', fromCents(info.paid_cents),
    name + ' — ' + MONTHS_FULL[m - 1] + '. ' + expected + (vat ? '. ' + vatWords : '')
    + (applies ? '' : '. ' + periodReason(c)),
    (cents) => ctx.savePayment(c.id, m, cents), 74,
    name + ' — ' + MONTHS_FULL[m - 1].toLowerCase()));
  if (vat) {
    // Защо сумата е по-висока личи БЕЗ цвят: символ за окото…
    td.appendChild(el('span', { class: 'fees-vat-mark', 'aria-hidden': 'true', style: 'margin-left:3px;color:var(--purple);font-weight:700', text: '✻' }));
    // …и дума за екранния четец (цветът и знакът не се четат).
    td.appendChild(el('span', { class: 'sr-only', text: vatWords }));
  }
  // Плащане в месец извън периода: не го крием (парите не изчезват от екрана),
  // но го отбелязваме — иначе изглежда като редовно начисление.
  if (!applies) {
    td.appendChild(el('span', { class: 'fees-na-mark', 'aria-hidden': 'true', style: 'margin-left:3px;color:var(--faint)', text: '·' }));
    td.appendChild(el('span', { class: 'sr-only', text: periodReason(c) }));
  }
  return td;
}

function annualRow(c, ctx) {
  const name = c.label || ('№ ' + c.id);
  const tr = el('tr', { 'data-client-id': String(c.id) });
  tr.appendChild(el('td', {},
    el('div', { style: 'display:flex;gap:6px;align-items:center' },
      el('strong', { text: name }),
      el('button', {
        class: 'btn sm ghost', type: 'button', text: '✎',
        title: 'Редакция на клиента', 'aria-label': 'Редакция на „' + name + '"',
        onClick: () => clientForm(ctx.wrap, c),
      }))));
  if (colOn('signature')) tr.appendChild(el('td', { text: c.signature || '' }));
  if (colOn('accountant')) tr.appendChild(el('td', { text: c.accountant_name || '—' }));
  tr.appendChild(el('td', { text: eur(c.fee_base_cents) }));
  // Плащането е ЕДНО за годината — затова month: 0, не дванайсет клетки.
  tr.appendChild(el('td', { class: 'fees-annual-paid' },
    amountInput('fees-amount', fromCents(c.paid_cents), 'Платено за годишното приключване — ' + name,
      (cents) => ctx.savePayment(c.id, ANNUAL_MONTH, cents), undefined,
      name + ' — годишно приключване')));
  const debtTd = paintDebt(el('td', {}), c.debt_cents);
  ctx.debtCells.set(c.id, debtTd);
  tr.appendChild(debtTd);
  return tr;
}

// ---- Клиент ----------------------------------------------------------------

// Тялото за POST/PUT се сглобява НА ЕДНО МЯСТО: и формата, и полетата на реда
// пращат целия запис, за да не занули сървърът поле, което просто не сме
// подали (заварения капан „PUT без company_id" от другите раздели).
function clientPayload(c, over) {
  const base = {
    label: c.label || '',
    kind: c.kind === 'annual' ? 'annual' : 'monthly',
    signature: c.signature || '',
    accountant_name: c.accountant_name || '',
    blanka_firm_id: c.blanka_firm_id == null || c.blanka_firm_id === '' ? null : Number(c.blanka_firm_id),
    fee_base_cents: Number(c.fee_base_cents) || 0,
    fee_active_cents: Number(c.fee_active_cents) || 0,
    // Периодът пътува с всеки запис — иначе смяна на такса D от реда би
    // занулила „от май" и клиентът пак щеше да се начислява от януари.
    start_month: monthOrNull(c.start_month),
    end_month: monthOrNull(c.end_month),
    note: c.note || '',
    active: c.active !== false,
    sort_order: Number(c.sort_order) || 0,
  };
  return Object.assign(base, over || {});
}

// Избор на месец по име. Празната опция значи „целогодишно" — тя е ПЪРВА и
// изрично назована, за да не изглежда като недовършен избор.
function monthSelect(cls, current) {
  const cur = monthOrNull(current);
  return el('select', { class: cls },
    el('option', { value: '', text: '(целогодишно)', selected: !cur }),
    ...MONTHS_FULL.map((name, i) => el('option', { value: i + 1, text: name, selected: cur === i + 1 })));
}

function clientForm(wrap, existing) {
  const it = existing || {};
  const f = {
    label: input({ value: it.label || '', placeholder: 'напр. Аско 2 — банка' }),
    kind: el('select', {},
      el('option', { value: 'monthly', text: 'Месечна такса', selected: it.kind !== 'annual' }),
      el('option', { value: 'annual', text: 'Годишно приключване', selected: it.kind === 'annual' })),
    // Празна опция по подразбиране: досега първият запис тихо получаваше „БВ",
    // защото първата опция печели, когато нищо не е избрано (В.1).
    signature: el('select', {},
      el('option', { value: '', text: '— изберете —', selected: !it.signature }),
      ...SIGNATURES.map((sg) => el('option', { value: sg.code, text: sigLabel(sg.code), selected: it.signature === sg.code }))),
    accountant: input({ value: it.accountant_name || '', placeholder: 'име на счетоводителя' }),
    base: input({ class: 'fees-fee-base', type: 'text', inputmode: 'decimal', value: fromCents(it.fee_base_cents), placeholder: '0,00' }),
    active: input({ class: 'fees-fee-active', type: 'text', inputmode: 'decimal', value: fromCents(it.fee_active_cents), placeholder: '0,00' }),
    blanka: input({ type: 'text', inputmode: 'numeric', value: it.blanka_firm_id != null ? String(it.blanka_firm_id) : '', placeholder: 'номер от бланката (по избор)' }),
    // Месец, не дата: избор по ИМЕ. Свободно число кани грешки („13"), а
    // native <input type="date"> е забранен в проекта.
    start: monthSelect('fees-start-month', it.start_month),
    end: monthSelect('fees-end-month', it.end_month),
    sort: input({ type: 'text', inputmode: 'numeric', value: it.sort_order != null ? String(it.sort_order) : '', placeholder: 'напр. 10' }),
    note: el('textarea', { rows: 2, placeholder: 'Бележка (по избор)' }, it.note || ''),
    isActive: el('input', { type: 'checkbox', checked: it.active !== false }),
  };

  const body = el('div', {},
    field('Име на клиента', f.label),
    el('div', { class: 'field-row' }, field('Вид', f.kind), field('Подпис', f.signature)),
    el('div', { class: 'field-row' }, field('Счетоводител', f.accountant), field('Номер в бланката', f.blanka)),
    el('div', { class: 'field-row' },
      field('Основна такса (D)', f.base, { size: 's' }),
      field('ДДС такса (E) — за месеците с активна ДДС дейност', f.active, { size: 's' })),
    el('div', { class: 'field-row' },
      field('Начислява се от месец', f.start, { size: 'm' }),
      field('Начислява се до месец', f.end, { size: 'm' })),
    el('div', { class: 'cal-hint', style: 'padding:0 4px 8px', text: 'Оставете „(целогодишно)", ако клиентът се начислява цялата година. Месеците извън периода не се дължат и в матрицата излизат приглушени.' }),
    el('div', { class: 'field-row' }, field('Подредба', f.sort, { size: 's' }),
      el('div', { class: 'field' }, el('label', { text: 'Активен' }),
        el('label', { style: 'display:flex;gap:6px;align-items:center' }, f.isActive, 'Води се в списъка'))),
    el('div', { class: 'cal-hint', style: 'padding:0 4px 8px', text: 'Подредба: по-малко число = по-нагоре в таблицата. Празно = по азбучен ред.' }),
    field('Бележка', f.note));

  const save = async () => {
    const name = f.label.value.trim();
    if (!name) { toast('Въведете име на клиента.', true); return; }
    const baseC = toCents(f.base.value);
    const activeC = toCents(f.active.value);
    if (baseC === null || activeC === null) { toast('Таксите се въвеждат с цифри, например 120 или 120,50.', true); return; }
    const startM = monthOrNull(f.start.value);
    const endM = monthOrNull(f.end.value);
    // Обърнат период („от Септември до Май") би направил цялата година
    // неприложима — по-добре да се каже сега, отколкото после да се чуди
    // защо клиентът не дължи нищо.
    if (startM && endM && endM < startM) { toast('Крайният месец е преди началния — проверете периода.', true); return; }
    const payload = clientPayload(it, {
      label: name,
      kind: f.kind.value === 'annual' ? 'annual' : 'monthly',
      signature: f.signature.value,
      accountant_name: f.accountant.value.trim(),
      blanka_firm_id: f.blanka.value.trim() === '' ? null : Number(f.blanka.value.trim()),
      fee_base_cents: baseC,
      fee_active_cents: activeC,
      start_month: startM,
      end_month: endM,
      note: f.note.value.trim(),
      active: f.isActive.checked,
      sort_order: Number(f.sort.value.trim()) || 0,
    });
    try {
      if (it.id) await api('/fees/clients/' + it.id, { method: 'PUT', body: JSON.stringify(payload) });
      else await api('/fees/clients', { method: 'POST', body: JSON.stringify(payload) });
      closeModal(); toast('Записано.'); draw(wrap);
    } catch (e) { toast(e.message, true); }
  };

  const remove = async () => {
    const ok = await confirmDialog(
      'Да изтрия ли „' + (it.label || '') + '" от таксите? Плащанията и корекциите по този клиент се губят.',
      { okText: 'Изтрий клиента', danger: true }
    );
    if (!ok) return;
    try { await api('/fees/clients/' + it.id, { method: 'DELETE' }); closeModal(); toast('Клиентът е изтрит.'); draw(wrap); }
    catch (e) { toast(e.message, true); }
  };

  openModal({
    title: it.id ? 'Клиент в таксите' : 'Нов клиент в таксите', body, wide: true,
    foot: el('div', {},
      it.id ? el('button', { class: 'btn sm danger', type: 'button', text: 'Изтрий клиента', style: 'margin-right:auto', onClick: remove }) : null,
      el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', type: 'button', text: 'Запази', style: 'margin-left:8px', onClick: save })),
  });
}

// ---- Корекции: ДНЕВНИК -----------------------------------------------------
//
// ЗАЩО дневник, а не едно число: „Корекции −5,00 €" на реда не казва нито кога,
// нито защо. Собственикът трябва да вижда ВСЯКА корекция — дата, защо, колко —
// и да може да махне сгрешената. Затова числото на реда отваря този списък.

// Надписът на бутона се пипа на ЕДНО място: и при рисуване, и при опресняване
// след запис — иначе двете се разминават (същият урок като paintDebt).
function paintAdj(btn, c, name) {
  const list = Array.isArray(c.adjustments) ? c.adjustments : null;
  const n = list ? list.length : null;
  const sum = Number(c.adjustments_cents) || 0;
  clear(btn);
  btn.appendChild(el('span', { text: eur(sum) }));
  if (n) btn.appendChild(el('span', { style: 'margin-left:4px;color:var(--muted)', text: '(' + n + ')' }));
  const words = n === null ? '' : (n ? ' — ' + n + ' бр.' : ' — още няма нито една');
  btn.setAttribute('title', 'Корекции за ' + _year + ' г.' + words + '. Отваря дневника.');
  btn.setAttribute('aria-label', 'Корекции на „' + name + '" за ' + _year + ' г.: '
    + eur(sum) + words + '. Отваря дневника.');
}

// Един ред от дневника. Знакът е и СИМВОЛ (+/−), и ДУМА („начисляване" /
// „редукция") — цветът е трети, допълнителен носител, не единственият.
function adjRow(a, onRemove) {
  const cents = Number(a.amount_cents) || 0;
  const up = cents > 0; // плюс вдига дълга, минус го смъква
  const word = up ? 'начисляване' : 'редукция';
  const when = isoToBg(a.date) || '—';
  return el('tr', {},
    el('td', { style: 'white-space:nowrap', text: when }),
    el('td', { text: a.note || '—' }),
    el('td', {}, el('span', {
      class: 'tag ' + (up ? 'red' : 'green'),
      title: up ? 'Начисляване — вдига дълга.' : 'Редукция — смъква дълга.',
      text: word,
    })),
    el('td', {
      class: 'num',
      style: 'white-space:nowrap;font-weight:700;text-align:right;color:' + (up ? 'var(--red)' : 'var(--green)'),
    },
      // Знакът е отделен от сумата, за да не излезе двоен минус.
      el('span', { class: 'fees-adj-sign', 'aria-hidden': 'true', text: up ? '+' : '−' }),
      ' ',
      el('span', { text: eur(Math.abs(cents)) })),
    el('td', { style: 'text-align:right' }, el('button', {
      class: 'btn sm ghost', type: 'button', text: '×',
      title: 'Изтрий корекцията',
      'aria-label': 'Изтрий корекцията от ' + when + ' (' + word + ' ' + eur(Math.abs(cents)) + ')',
      onClick: () => onRemove(a),
    })));
}

function adjustmentsDialog(c, ctx) {
  const name = c.label || ('№ ' + c.id);
  let items = Array.isArray(c.adjustments) ? c.adjustments.slice() : [];
  const listBox = el('div', {});

  const dateEl = dateInput(todayIso());
  dateEl.className = 'fees-adj-date';
  const amountEl = input({ type: 'text', inputmode: 'decimal', class: 'fees-adj-amount', placeholder: 'напр. 12,50' });
  // (Б.9) Видът се ИЗБИРА, а не се кодира в знака на сумата: сбъркан минус
  // даваше обратен ефект и таблицата „лъжеше" до отваряне на дневника.
  // Сумата е винаги положителна — знака го слага кодът при записа.
  const kindName = 'fees-adj-kind-' + c.id;
  const kindDown = el('input', { type: 'radio', name: kindName, value: 'down', checked: true });
  const kindUp = el('input', { type: 'radio', name: kindName, value: 'up' });
  const kindHint = el('div', { class: 'cal-hint', style: 'padding:2px 0 0' });
  const syncKindHint = () => {
    kindHint.textContent = kindDown.checked
      ? 'Намалението смъква дълга на клиента (записва се с минус).'
      : 'Увеличението вдига дълга на клиента (записва се с плюс).';
  };
  kindDown.addEventListener('change', syncKindHint);
  kindUp.addEventListener('change', syncKindHint);
  syncKindHint();
  const kindBox = el('div', { class: 'fees-adj-kind', role: 'radiogroup', 'aria-label': 'Вид на корекцията' },
    el('label', {}, kindDown, el('span', { text: 'Намаление (отстъпка)' })),
    el('label', {}, kindUp, el('span', { text: 'Увеличение (доначисляване)' })));
  const noteEl = el('textarea', { rows: 2, class: 'fees-adj-note', placeholder: 'напр. отстъпка по договор' });

  const renderList = () => {
    clear(listBox);
    if (!items.length) {
      listBox.appendChild(el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Още няма корекции за ' + _year + ' г.' }));
      // Ако сборът не е нула, а списъкът е празен, това си личи — иначе
      // изглежда сякаш пари са изчезнали.
      if (Number(c.adjustments_cents)) {
        listBox.appendChild(el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'В сметката влиза сбор ' + eur(c.adjustments_cents) + '.' }));
      }
      return;
    }
    const tb = el('tbody');
    items.forEach((a) => tb.appendChild(adjRow(a, remove)));
    listBox.appendChild(el('table', { class: 'fees-adj-list' }, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Дата' }),
      el('th', { scope: 'col', text: 'Защо' }),
      el('th', { scope: 'col', text: 'Вид' }),
      el('th', { scope: 'col', style: 'text-align:right', text: 'Сума' }),
      el('th', { scope: 'col' }, el('span', { class: 'sr-only', text: 'Действие' })))), tb));
  };

  // След всяка промяна дърпаме прясното състояние от сървъра: и редът в
  // матрицата, и списъкът тук показват неговите числа, не наши сметки.
  const refresh = async () => {
    const fresh = await ctx.refreshNumbers();
    const row = fresh && (fresh.clients || []).find((x) => Number(x.id) === Number(c.id));
    if (row) {
      c.adjustments_cents = row.adjustments_cents;
      items = Array.isArray(row.adjustments) ? row.adjustments.slice() : [];
    }
    renderList();
  };

  async function remove(a) {
    const ok = await confirmDialog(
      'Да изтрия ли корекцията от ' + (isoToBg(a.date) || '—') + ' — „' + (a.note || '') + '" '
      + '(' + eur(a.amount_cents) + ')? Дългът на клиента се преизчислява веднага.',
      { okText: 'Изтрий корекцията', danger: true }
    );
    if (!ok) return;
    try {
      await api('/fees/adjustments/' + a.id, { method: 'DELETE' });
      toast('Корекцията е изтрита.');
      await refresh();
    } catch (e) { toast(e.message, true); }
  }

  const add = async () => {
    const raw = toCents(amountEl.value);
    if (raw === null) { toast('Въведете сума с цифри, например 12,50.', true); return; }
    // Абсолютна стойност: въведен по стар навик минус НЕ бива да обръща
    // избора („−50" при „Намаление" би станало +50).
    const size = Math.abs(raw);
    if (!size) { toast('Корекция с нула не променя нищо.', true); return; }
    const cents = kindDown.checked ? -size : size;
    const note = noteEl.value.trim();
    // Сървърът връща 400 без бележка. Спираме още тук: „защо" е целият смисъл
    // на дневника, а и човекът вижда какво липсва, вместо гола грешка.
    if (!note) { toast('Напишете защо е корекцията — без бележка не се записва.', true); noteEl.focus(); return; }
    const date = dateEl.dataset.iso || '';
    if (!date) { toast('Въведете дата във формат дд.мм.гггг.', true); dateEl.focus(); return; }
    try {
      await api('/fees/adjustments', {
        method: 'POST',
        body: JSON.stringify({ fee_client_id: c.id, year: _year, date, amount_cents: cents, note }),
      });
      amountEl.value = '';
      noteEl.value = '';
      toast((kindDown.checked ? 'Намаление' : 'Увеличение') + ' ' + eur(size) + ' е записано за „' + name + '".');
      await refresh();
    } catch (e) { toast(e.message, true); }
  };

  renderList();

  // openModal иска ОБЕКТ ({ title, body, foot }) — с два аргумента диалогът
  // мълчи. Този капан вече се е случвал в този файл.
  openModal({
    title: 'Корекции — ' + name + ' (' + _year + ' г.)', wide: true,
    body: el('div', {},
      el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px', text: 'Всяка корекция е отделен запис: избирате намаление или увеличение и пишете сумата без знак. Сборът им влиза в колоната „Корекции" на реда.' }),
      listBox,
      el('div', { style: 'border-top:1px solid var(--border);margin-top:10px;padding-top:10px' },
        el('div', { style: 'font-weight:700;margin-bottom:6px', text: 'Нова корекция' }),
        field('Вид на корекцията', kindBox),
        kindHint,
        el('div', { class: 'field-row', style: 'margin-top:8px' },
          field('Дата', dateEl, { size: 's' }),
          field('Сума', amountEl, { size: 's' })),
        field('Защо (задължително)', noteEl),
        el('button', { class: 'btn sm', type: 'button', text: '+ Добави корекцията', onClick: add }))),
    foot: el('div', {},
      el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal })),
  });
}

// ---- Анализи ---------------------------------------------------------------

// Имената и сумите в справките идват от сървъра с различни ключове според
// справката — четем ги меко, за да не се пука екранът при празен ред.
const nameOf = (r) => r.label || r.name || (r.id != null ? '№ ' + r.id : '—');
const debtOf = (r) => (r.debt_cents != null ? r.debt_cents : r.amount_cents);
const overOf = (r) => (r.overpaid_cents != null ? r.overpaid_cents
  : (r.debt_cents != null ? -Number(r.debt_cents) : r.amount_cents));

// ---- Докъде стигат числата -------------------------------------------------
//
// ЗАЩО стои най-отгоре: „Приход 1 780,00 €" без период се чете като „за цялата
// година" и всеки август някой пита защо е малко. through_month казва докъде е
// начислено (7 = до юли включително) и точно това пише тук — веднъж, над
// всички справки, вместо на всяко заглавие поотделно.
//
// Липсва ли through_month, месец НЕ се измисля: по-добре да пише само годината,
// отколкото грешен месец, на който някой ще повярва.
function throughNote(through) {
  const m = monthOrNull(through);
  return el('div', {
    class: 'cal-hint fees-through-note',
    style: 'padding:0 0 6px;font-weight:600',
    title: m
      ? 'Начисленията и разходите след ' + MONTHS_FULL[m - 1] + ' още не влизат в тези числа.'
      : 'Сървърът не казва до кой месец са начисленията за тази година.',
    text: m
      ? 'Данните са до ' + MONTHS_FULL[m - 1] + ' ' + _year + ' г. включително.'
      : 'Данните са за ' + _year + ' г. — сървърът не казва до кой месец са начисленията.',
  });
}

function twoColumnPanel(title, firstHead, cls, rows) {
  const panel = el('div', { class: 'panel ' + cls });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: title })));
  if (!rows.length) { panel.appendChild(emptyState('€', 'Няма данни за ' + _year + ' г.')); return panel; }
  const tb = el('tbody');
  rows.forEach((r) => tb.appendChild(el('tr', {},
    el('td', { text: r.name }),
    el('td', { class: 'num', style: Number(r.debt) > 0 ? 'color:var(--red);font-weight:700' : '', text: eur(r.debt) }),
    el('td', { class: 'num', text: eur(r.paid) }))));
  panel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
    el('th', { scope: 'col', text: firstHead }),
    el('th', { scope: 'col', class: 'num', text: 'Дълг' }),
    el('th', { scope: 'col', class: 'num', text: 'Платено' }))), tb));
  return panel;
}

function listPanel(title, cls, headLabel, amountHead, rows) {
  const panel = el('div', { class: 'panel ' + cls });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: title })));
  if (!rows.length) { panel.appendChild(emptyState('✔', 'Няма такива за ' + _year + ' г.')); return panel; }
  const tb = el('tbody');
  rows.forEach((r) => tb.appendChild(el('tr', {},
    el('td', { text: r.name }),
    el('td', { class: 'num', text: eur(r.amount) }))));
  panel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
    el('th', { scope: 'col', text: headLabel }),
    el('th', { scope: 'col', class: 'num', text: amountHead }))), tb));
  return panel;
}

// Приход по счетоводител ПО МЕСЕЦИ (13+ колони, затова в скролбокс като
// матрицата). Редът отдолу е сборът на ВСЯКА КОЛОНА.
//
// ЗАЩО има превключвател: досега в клетката стоеше платеното, а начисленото —
// само в title. На телефон title изобщо не се показва, тоест половината справка
// беше недостъпна. Сега таблицата показва ЕДНОТО, изрично назовано в
// заглавието, а изборът се помни. Двете числа остават заедно при посочване —
// превключвателят казва кое се ПОКАЗВА, не кое съществува.
function accountantMonthsPanel(rows) {
  const panel = el('div', { class: 'panel fees-by-accountant-monthly' });
  const heading = el('h2', { text: 'Приход по счетоводител и месец' });
  const headBar = el('div', { class: 'panel-head' }, heading);
  panel.appendChild(headBar);
  if (!rows.length) { panel.appendChild(emptyState('€', 'Няма данни за ' + _year + ' г.')); return panel; }

  // Истински бутони с aria-pressed: кой е избран се ЧЕТЕ, а не се познава по
  // цвят. (Гол div тук би бил невидим за клавиатурата и за екранния четец.)
  const btns = {};
  const modeBtn = (mode, label, hint) => {
    const b = el('button', {
      type: 'button', 'data-mode': mode, title: hint, text: label,
      onClick: () => { if (_accMode === mode) return; _accMode = mode; saveAccMode(); paint(); },
    });
    btns[mode] = b;
    return b;
  };
  headBar.appendChild(el('div', {
    role: 'group', 'aria-label': 'Какво да показва таблицата',
    style: 'display:flex;gap:6px;margin-left:auto',
  },
    modeBtn('paid', 'Платено', 'Показва постъпилото за месеца.'),
    modeBtn('expected', 'Начислено', 'Показва начисленото за месеца — независимо дали е платено.')));

  const box = el('div', { class: 'fees-scrollbox', style: 'overflow-x:auto' });
  const hint = el('div', { class: 'cal-hint' });
  panel.appendChild(box);
  panel.appendChild(hint);

  function table() {
    const isPaid = _accMode === 'paid';
    const word = isPaid ? 'постъпило' : 'начислено';
    const valOf = (info) => Number(isPaid ? info.paid_cents : info.expected_cents) || 0;

    const head = el('tr', {},
      el('th', { scope: 'col', style: TH, text: 'Счетоводител' }),
      ...MONTHS_SHORT.map((m, i) => el('th', { scope: 'col', style: TH + ';text-align:center', title: MONTHS_FULL[i], text: m })),
      el('th', { scope: 'col', style: TH + ';text-align:right', title: 'Общо ' + word + ' за годината', text: 'Общо' }));

    const tb = el('tbody');
    const sums = new Array(12).fill(0);
    let grand = 0;
    rows.forEach((r) => {
      // Клиентите без счетоводител са група като всяка друга — празен ред би
      // изглеждал като повреда в данните.
      const name = String(r.name || '').trim() || '(без счетоводител)';
      const tr = el('tr', {});
      tr.appendChild(el('th', { scope: 'row', style: TD + ';font-weight:600;text-align:left', text: name }));
      for (let m = 1; m <= 12; m++) {
        const info = (r.months || []).find((x) => Number(x.month) === m) || {};
        const v = valOf(info);
        sums[m - 1] += v;
        tr.appendChild(el('td', {
          'data-month': String(m), style: TD + ';text-align:center',
          title: name + ' · ' + MONTHS_FULL[m - 1] + ': платено ' + eur(info.paid_cents)
            + ', начислено ' + eur(info.expected_cents),
          text: eur(v),
        }));
      }
      // „Общо" идва ГОТОВО от сървъра — екранът не сумира втори път. И то
      // следва избора: платеното и начисленото са две различни числа.
      const total = Number(isPaid ? r.total_paid_cents : r.total_expected_cents) || 0;
      grand += total;
      tr.appendChild(el('td', {
        class: 'fees-acc-total', style: TD + ';text-align:right;font-weight:700',
        title: 'За годината: платено ' + eur(r.total_paid_cents) + ', начислено ' + eur(r.total_expected_cents),
        text: eur(total),
      }));
      tb.appendChild(tr);
    });

    const foot = el('tr', { class: 'fees-sum' },
      el('th', { scope: 'row', style: TD + ';text-align:left', text: 'Общо' }),
      ...sums.map((v, i) => el('td', {
        'data-month': String(i + 1), style: TD + ';text-align:center;font-weight:700',
        title: MONTHS_FULL[i] + ': общо ' + word + ' ' + eur(v), text: eur(v),
      })),
      el('td', { class: 'fees-acc-grand', style: TD + ';text-align:right;font-weight:700', text: eur(grand) }));

    return el('table', { class: 'fees-acc-table' },
      el('thead', {}, head), tb, el('tfoot', { style: 'border-top:2px solid var(--border)' }, foot));
  }

  // Пребоядисва се САМО таблицата: бутоните остават същите възли, за да не
  // изчезне фокусът изпод пръста на човека, който работи с клавиатура.
  function paint() {
    const isPaid = _accMode === 'paid';
    heading.textContent = 'Приход по счетоводител и месец — ' + (isPaid ? 'платено' : 'начислено');
    ['paid', 'expected'].forEach((m) => {
      const on = _accMode === m;
      btns[m].className = 'btn sm' + (on ? '' : ' ghost');
      btns[m].setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    clear(box);
    box.appendChild(table());
    hint.textContent = isPaid
      ? 'Числото в клетката е ПОСТЪПИЛОТО за месеца. Начисленото се вижда с бутона „Начислено" (и при посочване). Долният ред е сборът на всяка колона.'
      : 'Числото в клетката е НАЧИСЛЕНОТО за месеца — независимо дали е платено. Постъпилото се вижда с бутона „Платено" (и при посочване). Долният ред е сборът на всяка колона.';
  }

  paint();
  return panel;
}

// ---- Разходи: заплати, осигуровки и другите --------------------------------
//
// ЗАЩО ги има: анализите показваха само приходи, тоест отговаряха на половин
// въпрос. Собственикът гледа какво ОСТАВА.
//
// Категориите ИДВАТ ОТ СЪРВЪРА (GET /api/fees/expenses → categories:
// [{ key, label, person }]). Нарочно има ЕДИН списък за двете страни: ключът е
// това, което сървърът приема при запис, надписът е негов (за да не се разминат
// „Заплати" тук и „Заплата" там), а `person` казва за кои категории полето
// „човек" изобщо значи нещо. Списъкът долу е РЕЗЕРВА само за сървър, който още
// не го дава — подредбата обаче е наша: заплатите горе, „Друго" най-долу.
const FALLBACK_CATEGORIES = [
  { key: 'salary', label: 'Заплати', person: true },
  { key: 'insurance', label: 'Осигуровки', person: true },
  { key: 'rent', label: 'Наем' },
  { key: 'software', label: 'Софтуер и лицензи' },
  { key: 'bank', label: 'Банкови такси' },
  { key: 'phone', label: 'Телефон и интернет' },
  { key: 'office', label: 'Канцеларски' },
  { key: 'training', label: 'Обучения' },
  { key: 'transport', label: 'Транспорт' },
  { key: 'services', label: 'Външни услуги' },
  { key: 'tax', label: 'Данъци' },
  { key: 'other', label: 'Друго' },
];

// Списъкът за падащото меню и за надписите. Категория, която сървърът вече не
// предлага, но стои в стар разход, СЕ ДОБАВЯ накрая — иначе редът ѝ би останал
// с гол ключ на екрана.
function categoriesOf(ex, seen) {
  const given = ex && Array.isArray(ex.categories) && ex.categories.length ? ex.categories : FALLBACK_CATEGORIES;
  const list = given.map((c) => ({
    key: String((c && (c.key || c.category)) || '').trim(),
    label: (c && c.label) || String((c && (c.key || c.category)) || ''),
    person: !!(c && c.person),
  })).filter((c) => c.key);
  (seen || []).forEach((r) => {
    const key = String((r && (r.category || r.key)) || '').trim();
    if (!key || list.some((c) => c.key === key)) return;
    list.push({ key, label: (r && r.label) || key, person: false });
  });
  return list;
}

const catOf = (list, key) => (list || []).find((c) => c.key === key) || null;
const catLabel = (list, key) => (catOf(list, key) || {}).label || key || '—';

// Печалбата е единственото число на този екран, което може да е ОТРИЦАТЕЛНО.
// Затова знакът е СИМВОЛ (▼/▲ и −/+) плюс ДУМА — цветът е трети, допълнителен
// носител: човек, който не различава червено от зелено, пак трябва да види, че
// годината е на минус. Знакът е отделен от сумата, за да няма двоен минус
// (същият урок като в дневника на корекциите).
function signedMoney(cents) {
  const n = Number(cents) || 0;
  return (n < 0 ? '▼ −' : (n > 0 ? '▲ +' : '')) + eur(Math.abs(n));
}

function profitCell(cents, cls) {
  const n = Number(cents) || 0;
  const loss = n < 0;
  return el('td', {
    class: 'num ' + (cls || 'fees-profit') + (loss ? ' loss' : ''),
    style: 'white-space:nowrap;font-weight:700;' + (loss ? 'color:var(--red)' : ''),
    title: loss ? 'Разходите надвишават прихода — на загуба.' : 'Остатъкът след разходите.',
  },
    el('span', { text: signedMoney(n) }),
    // Цветът и знакът не се четат от екранен четец — думата се чете.
    loss ? el('span', { class: 'sr-only', text: ' — загуба' }) : null);
}

function profitCard(cents) {
  const n = Number(cents) || 0;
  const loss = n < 0;
  const card = statCard('Печалба', signedMoney(n),
    loss ? 'ЗАГУБА за ' + _year + ' г.' : 'печалба за ' + _year + ' г.',
    { warn: loss, subAlarm: loss });
  card.classList.add('fees-card-profit');
  return card;
}

// Приход, разход и печалба по месеци. Трите числа стоят на ЕДИН ред, защото
// въпросът е „този месец на плюс ли бяхме" — три отделни таблици не му отговарят.
function profitMonthsPanel(profit) {
  const panel = el('div', { class: 'panel fees-profit-panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Приход, разход и печалба по месеци — ' + _year })));

  const by = new Map();
  (profit.by_month || []).forEach((r) => {
    const m = monthOrNull(r && r.month);
    if (m) by.set(m, r);
  });

  const tb = el('tbody');
  for (let m = 1; m <= 12; m++) {
    const r = by.get(m) || {};
    tb.appendChild(el('tr', { 'data-month': String(m) },
      el('td', { text: MONTHS_FULL[m - 1] }),
      el('td', { class: 'num fees-income', text: eur(r.income_cents) }),
      el('td', { class: 'num fees-expense', text: eur(r.expense_cents) }),
      profitCell(r.profit_cents)));
  }

  panel.appendChild(el('table', { class: 'fees-profit-months' },
    el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Месец' }),
      el('th', { scope: 'col', class: 'num', text: 'Приход' }),
      el('th', { scope: 'col', class: 'num', text: 'Разход' }),
      el('th', { scope: 'col', class: 'num', text: 'Печалба' }))),
    tb,
    el('tfoot', { style: 'border-top:2px solid var(--border)' }, el('tr', {},
      el('th', { scope: 'row', style: 'font-weight:700', text: 'Общо за ' + _year }),
      el('td', { class: 'num', style: 'font-weight:700', text: eur(profit.total_income_cents) }),
      el('td', { class: 'num', style: 'font-weight:700', text: eur(profit.total_expense_cents) }),
      profitCell(profit.total_profit_cents)))));
  panel.appendChild(el('div', { class: 'cal-hint', text: 'Печалбата е приходът минус разходите за месеца. Знакът ▼ и червеното значат загуба.' }));
  return panel;
}

// ---- Кръгова диаграма на разходите -----------------------------------------
//
// Искане на собственика: „искам да има разбивка на разходите (като pie chart с
// цифри)". Ключовата дума е ЦИФРИ: кръгът показва съотношението на око, но
// числата остават в таблицата до него — тя е и легендата.
//
// ЗАЩО собствен помощник за SVG: document.createElement('svg') прави елемент в
// HTML пространството от имена и браузърът го приема за НЕПОЗНАТ етикет —
// нищо не се рисува, а тестът „има svg" минава. SVG живее в свое пространство и
// се създава само с createElementNS. el() от shared/dom.js остава за HTML и не
// се пипа заради един екран.
const SVG_NS = 'http://www.w3.org/2000/svg';
function svgEl(tag, attrs, text) {
  const node = document.createElementNS(SVG_NS, tag);
  for (const k in (attrs || {})) {
    const v = attrs[k];
    if (v == null || v === false) continue;
    node.setAttribute(k, String(v));
  }
  if (text != null) node.appendChild(document.createTextNode(String(text)));
  return node;
}

// ЗАЩО тези цветове: това е палитрата на приложението (--accent, --blue,
// --amber, --purple, --green, --red…) с добавени няколко, за да не се повтарят
// съседни дялове. Цветът обаче НЕ носи смисъл сам — до всяко име в легендата
// стои неговото кръгче, а името е истински текст.
const PIE_COLORS = ['#0f766e', '#1d4ed8', '#b45309', '#6d28d9', '#15803d',
  '#b91c1c', '#0891b2', '#c2410c', '#4d7c0f', '#9d174d'];
// Слетите дребни дялове са сиви: те не са категория, а „останалото".
const PIE_SMALL_COLOR = '#64748b';
// Под колко процента дялът се губи в кръга. Такива дялове се СЛИВАТ — но само
// в рисунката; в числата всяка категория си остава отделен ред.
const PIE_SMALL_PCT = 2.5;
const PIE_SMALL_KEY = '__small__';
const PIE_SMALL_LABEL = 'Други (дребни дялове)';

// Процент за четене: един знак след запетаята, български разделител.
function pctText(v) {
  return (Math.round(Number(v) * 10) / 10).toFixed(1).replace('.', ',') + '%';
}

// Точка от окръжността за дял 0..1. Тръгва от 12 часа и върви по часовниковата
// стрелка — така окото чете кръга като циферблат.
function piePoint(cx, cy, r, frac) {
  const a = (frac * 2 * Math.PI) - Math.PI / 2;
  const round = (n) => Math.round(n * 100) / 100;
  return [round(cx + r * Math.cos(a)), round(cy + r * Math.sin(a))];
}

// Един дял като път. Цял кръг НЕ се рисува така: началото и краят съвпадат и
// дъгата се изражда в нищо — затова 100 % дял излиза като <circle> (виж долу).
function pieArc(cx, cy, r, from, to) {
  const [x0, y0] = piePoint(cx, cy, r, from);
  const [x1, y1] = piePoint(cx, cy, r, to);
  const large = (to - from) > 0.5 ? 1 : 0;
  return 'M ' + cx + ' ' + cy + ' L ' + x0 + ' ' + y0
    + ' A ' + r + ' ' + r + ' 0 ' + large + ' 1 ' + x1 + ' ' + y1 + ' Z';
}

function pieChart(slices, total, ariaLabel) {
  const cx = 60, cy = 60, r = 54;
  const svg = svgEl('svg', {
    class: 'fees-pie', viewBox: '0 0 120 120', role: 'img', 'aria-label': ariaLabel,
    style: 'width:190px;height:190px;max-width:100%;flex:0 0 auto',
  });
  let acc = 0;
  slices.forEach((s) => {
    const frac = s.amount / total;
    const whole = slices.length === 1 || frac >= 0.9999;
    const node = whole
      ? svgEl('circle', { cx, cy, r })
      : svgEl('path', { d: pieArc(cx, cy, r, acc, acc + frac) });
    node.setAttribute('class', 'fees-pie-slice');
    node.setAttribute('data-category', s.key);
    node.setAttribute('fill', s.color);
    // Тънък кант в цвета на фона: съседни дялове не се сливат в едно петно.
    node.setAttribute('style', 'stroke:var(--surface);stroke-width:1');
    // Подсказка при посочване — иначе кръгът е ням за мишката.
    node.appendChild(svgEl('title', {}, s.title));
    svg.appendChild(node);
    acc += frac;
  });
  return svg;
}

// Разбивка по категории — КРЪГОВА ДИАГРАМА + числата до нея.
//
// Подредбата е на СПИСЪКА (заплатите горе), а не по размер: така всеки месец
// таблицата изглежда еднакво и се сравнява на око. Диаграмата следва СЪЩИЯ ред,
// за да съвпадат цветовете с редовете.
//
// Процентът идва ГОТОВ от сървъра (`share`, един знак, сборът е точно 100.0).
// Липсва ли (стар сървър), се смята тук — процентът е изрично искане и не бива
// да изчезва само защото полето още го няма.
function categoryPanel(list, rows) {
  const panel = el('div', { class: 'panel fees-expenses-by-category' });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Разходи по категории — ' + _year })));
  const by = new Map();
  (rows || []).forEach((r) => {
    const key = String((r && r.category) || '').trim();
    if (key) by.set(key, r);
  });
  const present = (list || []).filter((c) => by.has(c.key));
  if (!present.length) { panel.appendChild(emptyState('€', 'Още няма записани разходи за ' + _year + ' г.')); return panel; }

  const shown = present.map((c) => {
    const src = by.get(c.key) || {};
    return { key: c.key, label: c.label, amount: Number(src.amount_cents) || 0, share: Number(src.share) };
  });
  const total = shown.reduce((t, s) => t + s.amount, 0);
  shown.forEach((s) => {
    // Числото на сървъра е истината; своя сметка се прави само когато го няма.
    s.pct = Number.isFinite(s.share) ? s.share : (total > 0 ? (s.amount / total) * 100 : null);
  });

  // Кръгът се рисува само от ПОЛОЖИТЕЛНИТЕ суми: отрицателна категория (върната
  // сума, поправка) няма дял от кръг и не бива да завърта дъгите наопаки.
  const drawable = shown.filter((s) => s.amount > 0);
  const pieTotal = drawable.reduce((t, s) => t + s.amount, 0);
  // Дребните се сливат само ако са ПОВЕЧЕ ОТ ЕДИН: един слят дял е същият дял
  // под друго име, само че вече без своето.
  const smalls = drawable.filter((s) => s.pct != null && s.pct < PIE_SMALL_PCT);
  const merged = new Set(smalls.length >= 2 ? smalls.map((s) => s.key) : []);
  shown.forEach((s, i) => {
    s.merged = merged.has(s.key);
    s.color = s.merged ? PIE_SMALL_COLOR : PIE_COLORS[i % PIE_COLORS.length];
  });

  const amountOf = (s) => eur(s.amount) + (s.pct == null ? '' : ' (' + pctText(s.pct) + ')');
  const slices = drawable.filter((s) => !s.merged)
    .map((s) => ({ key: s.key, color: s.color, amount: s.amount, title: s.label + ': ' + amountOf(s) }));
  if (merged.size) {
    const sum = smalls.reduce((t, s) => t + s.amount, 0);
    slices.push({
      key: PIE_SMALL_KEY, color: PIE_SMALL_COLOR, amount: sum,
      // Подсказката изрежда какво точно влиза вътре — иначе слятото парче е
      // число без произход.
      title: PIE_SMALL_LABEL + ': ' + eur(sum) + ' — ' + smalls.map((s) => s.label + ' ' + amountOf(s)).join(', '),
    });
  }

  // Достъпното име изрежда ВСИЧКИ категории със сумите им (не само нарисуваните
  // дялове): екранният четец трябва да получи същото, което окото вижда в
  // числата отдясно.
  const ariaLabel = 'Кръгова диаграма: разходи по категории за ' + _year + ' г. Общо ' + eur(total) + '. '
    + shown.map((s) => s.label + ' ' + amountOf(s)).join('; ') + '.';

  const chartBox = el('div', { style: 'flex:0 0 auto;display:flex;flex-direction:column;gap:6px;align-items:center' });
  if (pieTotal > 0) {
    chartBox.appendChild(pieChart(slices, pieTotal, ariaLabel));
    if (merged.size) {
      chartBox.appendChild(el('div', {
        class: 'cal-hint fees-pie-note', style: 'padding:0;text-align:center;max-width:190px',
        text: 'Дяловете под ' + pctText(PIE_SMALL_PCT) + ' са слети в едно парче — в таблицата стоят поотделно.',
      }));
    }
  } else {
    // Кръг от нула дялове не значи нищо и изглежда като повреда. Казваме защо
    // го няма — числата отдясно си остават.
    chartBox.appendChild(el('div', {
      class: 'cal-hint fees-pie-empty', style: 'padding:0;max-width:220px;white-space:normal',
      text: 'Записаните разходи за ' + _year + ' г. правят ' + eur(total)
        + ' — няма какво да се нарисува. Диаграмата се появява при първия разход над нула.',
    }));
  }

  const tb = el('tbody');
  shown.forEach((s) => {
    tb.appendChild(el('tr', { 'data-category': s.key },
      el('td', { style: 'white-space:normal' },
        // Кръгчето свързва реда с дяла, но е УКРАСА: смисълът е в името до него.
        el('span', {
          class: 'fees-pie-swatch', 'aria-hidden': 'true',
          style: 'display:inline-block;width:10px;height:10px;border-radius:50%;'
            + 'margin-right:7px;background:' + s.color,
        }),
        el('span', { text: s.label }),
        // Слятото парче няма свой цвят в кръга — редът го КАЗВА, вместо човек
        // да търси сиво кръгче, което значи три различни неща.
        s.merged ? el('span', {
          class: 'fees-cat-merged', style: 'font-size:12px;color:var(--muted)',
          text: ' — в диаграмата е слят в „' + PIE_SMALL_LABEL + '"',
        }) : null),
      el('td', { class: 'num fees-cat-amount', text: eur(s.amount) }),
      el('td', {
        class: 'num fees-cat-share',
        title: s.pct == null ? 'Няма общ разход, спрямо който да се смята дял.' : null,
        text: s.pct == null ? '—' : pctText(s.pct),
      })));
  });

  const pctSum = shown.reduce((t, s) => t + (s.pct == null ? 0 : s.pct), 0);
  const table = el('table', { style: 'flex:1 1 260px;min-width:260px' },
    el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Категория' }),
      el('th', { scope: 'col', class: 'num', text: 'Разход' }),
      el('th', { scope: 'col', class: 'num', title: 'Каква част от общия разход е това', text: 'Дял' }))),
    tb,
    el('tfoot', { style: 'border-top:2px solid var(--border)' }, el('tr', {},
      el('th', { scope: 'row', style: 'font-weight:700', text: 'Общо' }),
      el('td', { class: 'num fees-cat-amount', style: 'font-weight:700', text: eur(total) }),
      el('td', { class: 'num fees-cat-share', style: 'font-weight:700', text: pctText(pctSum) }))));

  panel.appendChild(el('div', {
    style: 'display:flex;gap:18px;flex-wrap:wrap;align-items:flex-start',
  }, chartBox, table));
  panel.appendChild(el('div', {
    class: 'cal-hint',
    text: 'Таблицата е и легендата на кръга: кръгчето до всяко име е неговият цвят в диаграмата, '
      + 'а до него стоят сумата и делът ѝ от общия разход.',
  }));
  return panel;
}

// ---- Разходи по хора: заплати / осигуровки / други / споделени -------------
//
// ЗАЩО разбивка по колони: „Разход 1 950,00 €" на един ред не отговаря на
// въпроса, който собственикът задава — колко от това е заплата и колко
// осигуровки. Числата идват ГОТОВИ от сървъра (salary_cents / insurance_cents /
// other_cents / shared_cents); тук не се смята нищо освен сборовете на колоните.
//
// ЗАЩО превключвател месец/година: „колко ми струва човекът ТОЗИ месец" се
// сверява срещу платежното, а „колко за годината досега" е годишната сметка.
// Едно число не отговаря на двата въпроса. Годишната е ДО through_month и
// точно това пише на бутона — иначе „за годината" се чете като „до декември".
//
// ЗАЩО и колона „Споделени": наемът, абонаментите и софтуерът се въвеждат БЕЗ
// лице, защото ги ползват всички. Таблицата показваше само разходите С ПОСОЧЕНО
// ЛИЦЕ и затова сборът ѝ беше по-малък от общия разход на кантората — дословно
// оплакването на собственика („не излизат другите разходи по хора"). Сега
// сървърът дава дела на всеки (shared_cents) и amount_cents е ПРЯКО + СПОДЕЛЕНО,
// тоест сборът на колоната „Общо" трябва да е равен на целия разход.
//
// КАПАН: липсващо число НЕ е нула. Стар сървър дава само amount_cents — тогава
// в трите колони пише „—". Нула там би значела „няма заплата", а истината е
// „не знам как се дели". По същата причина колоната „Споделени" изобщо не се
// показва, ако сървърът не праща shared_cents (виж hasSharedCost).

// Меко четене на едно перо: липсва ли изобщо ключът → null („не знам"), не 0.
function cent(v) { return (v == null || v === '') ? null : (Number(v) || 0); }

function personBreakdown(src) {
  return {
    salary: cent(src && src.salary_cents),
    insurance: cent(src && src.insurance_cents),
    other: cent(src && src.other_cents),
    shared: cent(src && src.shared_cents),
    // ВНИМАНИЕ: смисълът на amount_cents се смени — вече е пряко + споделено.
    total: Number((src && src.amount_cents) || 0) || 0,
  };
}

// ЕДНО място за въпроса „дава ли сървърът споделения дял". И таблицата, и
// Excel-ът стъпват на него: две отделни проверки биха се разминали и файлът
// щеше да носи колона, която на екрана я няма. Гледа се и в месеците, защото
// човек само със споделен дял може да го има само там.
export function hasSharedCost(rows) {
  return (rows || []).some((r) => cent(r && r.shared_cents) != null
    || (Array.isArray(r && r.months) && r.months.some((m) => cent(m && m.shared_cents) != null)));
}

// Липсващата стойност се показва с ДУМА („—"), не с нула: нулата значи „нищо
// не е дадено на този човек", а тирето — „сървърът не дели тази сума".
function centsCell(v, cls, extra) {
  return el('td', {
    class: 'num ' + (cls || ''),
    style: 'white-space:nowrap' + (extra || ''),
    title: v == null ? 'Сървърът не дава това число за този ред.' : null,
    text: v == null ? '—' : eur(v),
  });
}

// Какво Е споделеното — с ДУМИ, на едно място. Числото „100,00 €" до заплатата
// изглежда взето от нищото и се чете като втора заплата; затова обяснението
// стои и на заглавието на колоната, и в текста под таблицата.
const SHARED_WHAT = 'наем, софтуер и други общи разходи, разделени поравно между водещите клиенти';

function personPanel(rows, through, totalExpense) {
  const panel = el('div', { class: 'panel fees-expenses-by-person' });
  const heading = el('h2', {});
  const headBar = el('div', { class: 'panel-head' }, heading);
  panel.appendChild(headBar);

  const items = (rows || []).map((r) => ({
    name: personOf(r) || '(без човек)',
    year: personBreakdown(r),
    months: Array.isArray(r && r.months) ? r.months : null,
  }));
  // Стар сървър не дели общите разходи — тогава колоната изобщо я няма. Нула или
  // „—" там биха се четели като „на този човек не се пада нищо общо", а истината
  // е, че сървърът още не смята това число.
  const hasShared = hasSharedCost(rows);
  if (!items.length) {
    heading.textContent = 'Разходи по хора — ' + _year;
    panel.appendChild(emptyState('€', 'Още няма разходи на нечие име за ' + _year + ' г.'));
    return panel;
  }

  // Превключвателят се показва САМО когато има какво да превключва. Бутон, зад
  // който стоят дванайсет нули, е по-лош от липсващ бутон — човек решава, че
  // през март не е плащал заплати.
  const hasMonths = items.some((i) => i.months);
  const thr = monthOrNull(through);
  const curMonth = () => monthOrNull(_personMonth) || thr || 1;
  const scope = () => (hasMonths && _personScope === 'month' ? 'month' : 'year');

  const btns = {};
  let monthField = null;
  let monthSel = null;
  if (hasMonths) {
    // Истински бутони с aria-pressed: кой е избран се ЧЕТЕ, а не се познава по
    // цвят (правилото за достъпност в целия проект).
    const modeBtn = (id, hint) => {
      const b = el('button', {
        type: 'button', 'data-person-scope': id, title: hint,
        onClick: () => { if (scope() === id) return; _personScope = id; savePersonScope(); paint(); },
      });
      btns[id] = b;
      return b;
    };
    monthSel = el('select', { class: 'fees-person-month' },
      ...MONTHS_FULL.map((n, i) => el('option', { value: i + 1, text: n })));
    // Смяната на месеца сама включва месечния изглед: иначе човек избира „Март"
    // и таблицата под него не мърда — изглежда като счупен контрол.
    monthSel.addEventListener('change', () => {
      _personMonth = monthOrNull(monthSel.value) || 1;
      _personScope = 'month';
      savePersonScope();
      paint();
    });
    monthField = field('Месец', monthSel, { size: 'm' });
    headBar.appendChild(el('div', {
      role: 'group', 'aria-label': 'За кой период да са разходите',
      style: 'display:flex;gap:6px;align-items:flex-end;margin-left:auto',
    },
      monthField,
      modeBtn('month', 'Показва разхода само за избрания месец.'),
      modeBtn('year', 'Показва разхода за годината — до месеца, докъдето е начислено.')));
  }

  const box = el('div', {});
  // Предупреждението за разминаване стои МЕЖДУ таблицата и обяснението — точно
  // където окото пада, след като е прочело реда „Общо".
  const warn = el('div', {});
  const hint = el('div', { class: 'cal-hint' });
  panel.appendChild(box);
  panel.appendChild(warn);
  panel.appendChild(hint);

  // Числата за един човек според избрания период. Месец без запис е ИСТИНСКА
  // нула (нищо не е платено тогава), затова тук не се връща null.
  function figures(it) {
    if (scope() !== 'month') return it.year;
    const m = curMonth();
    const src = (it.months || []).find((x) => Number(x && x.month) === m);
    return src ? personBreakdown(src) : { salary: 0, insurance: 0, other: 0, shared: 0, total: 0 };
  }

  // Връща и таблицата, и сбора на колоната „Общо": сверката отдолу трябва да
  // гледа ТОЧНО числото, което е нарисувано, а не втора своя сметка.
  function table() {
    // Най-голямата сума отгоре: въпросът е „кой колко ми струва".
    const shown = items.map((it) => ({ name: it.name, f: figures(it) }))
      .sort((a, b) => (b.f.total - a.f.total) || a.name.localeCompare(b.name, 'bg'));

    const sums = { salary: null, insurance: null, other: null, shared: null, total: 0 };
    const add = (key, v) => { if (v != null) sums[key] = (sums[key] || 0) + v; };

    const tb = el('tbody');
    shown.forEach((r) => {
      add('salary', r.f.salary); add('insurance', r.f.insurance); add('other', r.f.other);
      add('shared', r.f.shared);
      sums.total += r.f.total;
      tb.appendChild(el('tr', { 'data-person': r.name },
        el('td', { text: r.name }),
        centsCell(r.f.salary, 'fees-person-salary'),
        centsCell(r.f.insurance, 'fees-person-insurance'),
        centsCell(r.f.other, 'fees-person-other'),
        // Клетката и заглавието се решават от ЕДНА проверка (hasShared) — иначе
        // числата увисват под чужда колона (същият урок като сборовете в
        // матрицата).
        hasShared ? centsCell(r.f.shared, 'fees-person-shared') : null,
        centsCell(r.f.total, 'fees-person-total', ';font-weight:700')));
    });

    const node = el('table', { class: 'fees-person-table' },
      el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: 'Човек' }),
        el('th', { scope: 'col', class: 'num', text: 'Заплати' }),
        el('th', { scope: 'col', class: 'num', text: 'Осигуровки' }),
        el('th', { scope: 'col', class: 'num', title: 'Всичко останало на негово име', text: 'Други' }),
        hasShared ? el('th', {
          scope: 'col', class: 'num',
          title: 'Дял от общите разходи — ' + SHARED_WHAT + '. Тези разходи не са на нечие име, защото ги ползват всички.',
          text: 'Споделени',
        }) : null,
        el('th', { scope: 'col', class: 'num', text: 'Общо' }))),
      tb,
      el('tfoot', { style: 'border-top:2px solid var(--border)' }, el('tr', {},
        el('th', { scope: 'row', style: 'font-weight:700', text: 'Общо' }),
        centsCell(sums.salary, 'fees-person-salary', ';font-weight:700'),
        centsCell(sums.insurance, 'fees-person-insurance', ';font-weight:700'),
        centsCell(sums.other, 'fees-person-other', ';font-weight:700'),
        hasShared ? centsCell(sums.shared, 'fees-person-shared', ';font-weight:700') : null,
        centsCell(sums.total, 'fees-person-total', ';font-weight:700'))));
    return { node, total: sums.total };
  }

  // Пребоядисва се САМО таблицата: бутоните остават същите възли, за да не
  // изчезне фокусът изпод пръста на човека, който работи с клавиатура.
  function paint() {
    const isMonth = scope() === 'month';
    const m = curMonth();
    const period = isMonth ? MONTHS_FULL[m - 1] + ' ' + _year
      : _year + ' г.' + (thr ? ' (до ' + MONTHS_FULL[thr - 1] + ')' : '');
    heading.textContent = 'Разходи по хора — ' + period;
    if (hasMonths) {
      monthSel.value = String(m);
      // Изборът на месец няма смисъл в годишния изглед — вместо мъртъв контрол
      // полето просто не се показва.
      monthField.setAttribute('style', isMonth ? '' : 'display:none');
      const label = { month: 'За месец', year: 'За годината' + (thr ? ' (до ' + MONTHS_FULL[thr - 1] + ')' : '') };
      ['month', 'year'].forEach((id) => {
        const on = (id === 'month') === isMonth;
        btns[id].textContent = label[id];
        btns[id].className = 'btn sm' + (on ? '' : ' ghost');
        btns[id].setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    clear(box);
    const t = table();
    box.appendChild(t.node);

    // Сборът на колоната „Общо" ТРЯБВА да е равен на общия разход на кантората —
    // това е целият смисъл на новата колона. Разминат ли се, някой разход увисва
    // невидимо и по-добре екранът да го КАЖЕ, отколкото собственикът да го
    // открие с калкулатор (същият похват като сверката в таблото).
    //
    // Сверява се САМО в годишния изглед и САМО при нов сървър:
    //   • месечният сбор няма как да е равен на ГОДИШНИЯ разход — предупреждение
    //     там би излизало всеки месец, без да значи нещо;
    //   • стар сървър не дава споделеното изобщо, тоест разликата е известна и
    //     очаквана — тревога за нея просто гони собственика да търси
    //     несъществуващ проблем.
    clear(warn);
    const whole = totalExpense == null ? null : Number(totalExpense);
    if (!isMonth && hasShared && whole != null && whole !== t.total) {
      warn.appendChild(el('div', {
        class: 'cal-hint fees-person-mismatch',
        style: 'color:var(--red);font-weight:600;white-space:normal',
      },
        el('span', { 'aria-hidden': 'true', text: '⚠ ' }),
        'Колоните не се равняват: „Общо" по хората дава ' + eur(t.total)
        + ', а общият разход на кантората е ' + eur(whole) + '.'));
    }

    // „Споделени" се обяснява и с думи под таблицата: подсказка на заглавието се
    // вижда само с мишка, а собственикът гледа този панел и на телефон.
    const sharedNote = hasShared
      ? ' „Споделени" е делът от общите разходи — ' + SHARED_WHAT + '.'
        + (isMonth ? '' : ' Затова сборът на колоната „Общо" е целият разход на кантората, не само заплатите.')
      : '';
    hint.textContent = (isMonth
      ? 'Числата са само за ' + MONTHS_FULL[m - 1] + '.'
      : 'Числата са за цялата година' + (thr ? ' до ' + MONTHS_FULL[thr - 1] + ' включително' : '') + '.')
      + ' „Други" е всичко на негово име извън заплата и осигуровки.' + sharedNote;
  }

  paint();
  return panel;
}

// ---- Табло „Счетоводители" -------------------------------------------------
//
// ЗАЩО табло, а не поредна справка: собственикът иска ЕДИН ред на човек — колко
// клиента води, колко му начисляваме, колко струва и какво остава. Неговите
// думи: „все едно използвам филтъра при парите", но нарязано по човек. Три
// отделни таблици за същото се сравняват наум и не се сравняват.
//
// ПЛАТЕНОТО ГО НЯМА нарочно — дословно негово искане: „в колона остава да бъде
// начислено − общ разход (платеното може да го махнеш, не е нужно за целите)".
// Тоест таблото отговаря на „колко фактурираме на този човек и какво остава
// след разхода му", а постъпилото се гледа в матрицата и в справката по месеци.
//
// Числата идват от РАЗНИ части на един и същ отговор и ТУК се смята само едно:
//   • разход (пряк/споделен) — accountant_contribution;
//   • начислено за годината — by_accountant_monthly (единственият му носител);
//   • брой клиенти — списъкът на матрицата (вече зареден, виж _clientsCache);
//   • „Остава" = начислено − общ разход. СМЯТА СЕ ТУК, защото сървърът дава
//     друго (profit_cents = платено − разход) — виж boardRows.
//
// КАПАН 1: `matched: false` значи, че на това име няма клиенти в таксите.
// Празните числа тогава се четат като „не носи нищо" и уволняват невинен човек
// — затова на реда с ДУМИ пише защо е празно.
// КАПАН 2 (matched_by): сдвояването по НОМЕР е сигурно, по ИМЕ е
// ПРЕДПОЛОЖЕНИЕ — „Иван Петров" и „Иван петров" са един и същ човек само ако
// някой е познал правилно. Разликата стои на реда с ДУМИ (не с цвят и не само
// при посочване), защото от нея зависи дали числото до него значи нещо.
// КАПАН 3: липсващо число НЕ е нула. Няма ли ред в справката по месеци, в
// „Начислено" пише „—": нулата там би значела „не му начисляваме нищо" — и
// точно затова „Остава" също става „—", вместо разлика с измислена нула.

// „Каквото не е ничие". Празното име и този надпис са ЕДНО И СЪЩО: клиентите
// без счетоводител и разходите, които не се падат на никого. Сървърът ги
// нарича по единия начин, старите справки — по другия; тук стават един ключ,
// за да не се появят ДВА реда за една и съща група.
const UNALLOCATED = '(неразпределено)';
function accName(v) {
  const s = String(v == null ? '' : v).trim();
  return (s === UNALLOCATED || s === '(без счетоводител)') ? '' : s;
}

// Ключът за сдвояване между справките. Номерът на потребителя е сигурен; името
// е предположение и затова се сваля до малки букви — в таксите се пише на ръка
// и „Иван"/„иван" са един и същ човек.
function accKey(id, name) {
  const n = Number(id);
  if (n) return 'id:' + n;
  return 'name:' + accName(name).toLowerCase();
}

// Клиентите, сведени до „чий е" — от ВЕЧЕ заредената матрица. Броят после е на
// ВСИЧКИ клиенти (месечни + годишно приключване): „води го" не значи „месечен".
// Връща null, когато списъкът го няма — тогава в колоната пише „—", вместо да
// се излъже с нула.
export function clientCounts(clients) {
  if (!Array.isArray(clients)) return null;
  return clients.map((c) => ({
    id: Number(c && c.accountant_user_id) || 0,
    name: accName(c && c.accountant_name).toLowerCase(),
    // Целият клиент пътува заедно с ключа: клик върху ред от таблото отваря
    // ФИРМИТЕ му с таксите, а те се знаят само тук. Иначе диалогът щеше да
    // дърпа наново списък, който вече е в паметта.
    src: c,
  }));
}

// Кои от тях са на този ред.
//
// КАПАН: матрицата дава ИМЕ (accountant_name), а справките — НОМЕР
// (accountant_user_id). Сдвояване само по едното връща нули за всички. Затова
// правилото е: по НОМЕР, когато и двете страни го имат (сигурно); иначе по име.
//
// ЕДНО правило за брояча и за диалога: две отделни проверки биха се разминали и
// таблото щеше да пише „3 клиента", а списъкът да показва други две фирми.
export function clientsFor(list, row) {
  if (!list) return null;
  const name = String((row && row.name) || '').toLowerCase();
  const id = row && row.id;
  return list.filter((c) => ((id && c.id) ? c.id === id : c.name === name));
}

// Колко от тях са на този ред.
function countFor(list, row) {
  const found = clientsFor(list, row);
  return found ? found.length : null;
}

// Един ред на счетоводител от трите източника. `contribution` е ОСНОВАТА (по
// договор той е обединението „има приход" + „има разход" — всеки се появява,
// дори с нули). Липсва ли изобщо (стар сървър), редовете идват от справката по
// месеци, а колоните за разхода остават празни, вместо да се измислят.
export function boardRows(contribution, monthly, counts) {
  const byKey = new Map();
  (monthly || []).forEach((r) => byKey.set(accKey(r && r.accountant_user_id, r && r.name), r));

  const has = Array.isArray(contribution) && contribution.length;
  const base = (has ? contribution : (monthly || [])).map((r) => ({
    src: has ? r : null,
    key: accKey(r && r.accountant_user_id, r && r.name),
    id: Number(r && r.accountant_user_id) || 0,
    name: accName(r && r.name),
  }));

  const seen = new Set();
  const rows = [];
  base.forEach((b) => {
    if (seen.has(b.key)) return;
    seen.add(b.key);
    const c = b.src;
    const m = byKey.get(b.key) || null;
    // Начислено и общ разход — двете страни на „Остава". Липсва ли ЕДНАТА,
    // остатъкът е „—", а не разлика с измислена нула.
    const expected = m ? cent(m.total_expected_cents) : null;
    const cost = c ? (Number(c.cost_cents) || 0) : null;
    rows.push({
      key: b.key,
      // Номерът пътува С РЕДА, не само с ключа: диалогът „кои са неговите фирми"
      // сдвоява по същото правило като брояча, а без него щеше да пада обратно
      // към името и да губи фирмите на преименуван счетоводител.
      id: b.id,
      name: b.name,
      unallocated: b.name === '',
      matched: !c || c.matched !== false,
      matchedBy: c ? c.matched_by : null,
      clients: countFor(counts, b),
      expected,
      direct: c ? cent(c.direct_cost_cents) : null,
      shared: c ? cent(c.shared_cost_cents) : null,
      cost,
      // ЗАЩО се смята ТУК, а не се взима profit_cents от сървъра: сървърът дава
      // „платено − разход", а собственикът иска „НАЧИСЛЕНО − разход" („в колона
      // остава да бъде начислено − общ разход"). Двете числа са различни и
      // взимането на готовото щеше да показва старото правило под ново име.
      left: (expected == null || cost == null) ? null : expected - cost,
    });
  });
  return rows;
}

// Подредбата: „(неразпределено)" винаги най-долу — той не е човек и не се
// сравнява с останалите. Липсващото число („—") пада най-долу в своята група:
// иначе би изпреварило истинските нули и таблото щеше да лъже за подредбата.
export function sortBoardRows(rows, sort) {
  const dir = (sort && sort.dir) === 'asc' ? 1 : -1;
  const key = (sort && sort.key) || 'expected';
  const byName = (a, b) => a.name.localeCompare(b.name, 'bg');
  return (rows || []).slice().sort((a, b) => {
    if (a.unallocated !== b.unallocated) return a.unallocated ? 1 : -1;
    if (key === 'name') return byName(a, b) * dir;
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return byName(a, b);
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * dir || byName(a, b);
  });
}

// „Платено" ГО НЯМА по изрично искане на собственика („платеното може да го
// махнеш, не е нужно за целите"). Въпросът, на който таблото отговаря, е колко
// НАЧИСЛЯВАМЕ на човека и колко остава след разхода му — постъпилото се гледа
// в матрицата и в „Приход по счетоводител и месец".
const BOARD_COLS = [
  { id: 'name', label: 'Счетоводител', hint: 'Кой води клиентите. Редът се натиска и показва фирмите му.' },
  { id: 'clients', label: 'Клиенти', num: true, count: true, hint: 'Колко клиента води — брои се от таблицата „Такси" (месечни и годишни).' },
  { id: 'expected', label: 'Начислено', num: true, hint: 'Колко му начисляваме за годината — независимо дали е платено.' },
  { id: 'direct', label: 'Пряк разход', num: true, hint: 'Разход на негово име — заплата, осигуровки и другите.' },
  { id: 'shared', label: 'Споделен разход', num: true, hint: 'Дял от общите разходи (наем, софтуер…), разделени поравно.' },
  { id: 'cost', label: 'Разход общо', num: true, hint: 'Пряк плюс споделен разход.' },
  { id: 'left', label: 'Остава', num: true, hint: 'Начисленото минус общия разход.' },
];

function accountantsBoardPanel(ctx) {
  const panel = el('div', { class: 'panel fees-accountants-board' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Табло на счетоводителите — ' + _year })));

  const rows = boardRows(ctx.contribution, ctx.monthly, ctx.counts);
  if (!rows.length) { panel.appendChild(emptyState('€', 'Няма данни за ' + _year + ' г.')); return panel; }

  // Общото НАЧИСЛЕНО, както го дава справката по месеци — по ВСИЧКИТЕ ѝ редове,
  // не само по стигналите до таблото. Точно затова сверката има смисъл:
  // счетоводител без ред в „принос" изчезва от таблото и начисленото му увисва.
  const totalExpected = (ctx.monthly || []).reduce((t, r) => t + (Number(r && r.total_expected_cents) || 0), 0);
  const hasMonthly = !!(ctx.monthly && ctx.monthly.length);

  // Таблото е седем колони — превърта се хоризонтално като матрицата, вместо да
  // изтласка последната колона извън екрана.
  const box = el('div', { class: 'fees-scrollbox', style: 'overflow-x:auto' });
  const foot = el('div', {});
  panel.appendChild(box);
  panel.appendChild(foot);

  const byNameWhy = 'Числата са намерени по ИЗПИСАНО ИМЕ, а не по номер на потребител — '
    + 'това е предположение. Свържете разхода с човек от списъка, за да е сигурно.';
  const unmatchedWhy = 'На това име няма клиенти в таксите — числата не могат да се сдвоят. Проверете как е изписано.';

  function head(col) {
    const on = _accSort.key === col.id;
    const th = el('th', {
      scope: 'col', style: TH + (col.num ? ';text-align:right' : ''), title: col.hint,
      // Посоката се ЧЕТЕ от екранния четец, а не се познава по стрелката.
      'aria-sort': on ? (_accSort.dir === 'asc' ? 'ascending' : 'descending') : 'none',
    });
    th.appendChild(el('button', {
      type: 'button', class: 'btn sm ghost fees-board-sort', 'data-sort': col.id,
      style: 'font-weight:700;padding:2px 6px',
      'aria-label': 'Подреди по „' + col.label + '"'
        + (on ? (_accSort.dir === 'asc' ? ' — сега от малкото към голямото' : ' — сега от голямото към малкото') : ''),
      onClick: () => {
        if (_accSort.key === col.id) _accSort = { key: col.id, dir: _accSort.dir === 'asc' ? 'desc' : 'asc' };
        // Имената тръгват от А, числата — от най-голямото: това търси окото.
        else _accSort = { key: col.id, dir: col.id === 'name' ? 'asc' : 'desc' };
        saveAccSort();
        paint(col.id);
      },
    },
      el('span', { text: col.label }),
      on ? el('span', { 'aria-hidden': 'true', text: _accSort.dir === 'asc' ? ' ▲' : ' ▼' }) : null));
    return th;
  }

  // Броят клиенти е БРОЙ, не сума — минава покрай eur(), иначе „3" би станало
  // „0,03 €".
  const countCell = (n, cls) => el('td', {
    class: 'num ' + cls, style: TD + ';text-align:right',
    title: n == null ? 'Списъкът с клиенти още не е зареден за тази година.' : null,
    text: n == null ? '—' : String(n),
  });

  function paint(focusCol) {
    clear(box);
    clear(foot);
    const shown = sortBoardRows(rows, _accSort);
    const sums = { clients: null, expected: null, direct: null, shared: null, cost: null };
    const add = (k, v) => { if (v != null) sums[k] = (sums[k] || 0) + v; };

    const tb = el('tbody');
    shown.forEach((r) => {
      add('clients', r.clients); add('expected', r.expected);
      add('direct', r.direct); add('shared', r.shared); add('cost', r.cost);
      const byName = r.matched && r.matchedBy === 'name';
      const label = r.name || UNALLOCATED;
      const cells = [];
      cells.push(el('td', { style: TD + ';white-space:normal' },
        el('div', { text: label }),
        // „(неразпределено)" сам по себе си е надпис в скоби — казваме с ДУМИ
        // какво стои зад него, иначе редът изглежда като повреда в данните.
        r.unallocated ? el('div', {
          class: 'fees-board-unallocated',
          style: 'font-size:12px;color:var(--muted);white-space:normal',
          title: 'Клиенти без счетоводител и разходи, които не са на нечие име.',
          text: 'клиенти без счетоводител и разходи, които не са ничии',
        }) : null,
        // „На това име няма клиенти" стоеше в клетката „Платено". Тя вече я
        // няма, а обяснението ТРЯБВА да остане: без него празните числа се
        // четат като „не носи нищо" и уволняват невинен човек.
        r.matched ? null : el('div', {
          class: 'fees-unmatched', title: unmatchedWhy,
          style: 'font-size:12px;color:var(--muted);white-space:normal',
        },
          el('span', { 'aria-hidden': 'true', text: '⚠ ' }),
          'няма клиенти на това име'),
        byName ? el('div', {
          class: 'fees-matched-name', title: byNameWhy,
          style: 'font-size:12px;color:var(--muted);white-space:normal',
        },
          el('span', { 'aria-hidden': 'true', text: '≈ ' }),
          'сдвоено по име, не по номер') : null));
      cells.push(countCell(r.clients, 'fees-board-clients'));
      cells.push(centsCell(r.expected, 'fees-board-expected', ';padding:4px 7px'));
      cells.push(centsCell(r.direct, 'fees-board-direct', ';padding:4px 7px'));
      cells.push(centsCell(r.shared, 'fees-board-shared', ';padding:4px 7px'));
      cells.push(centsCell(r.cost, 'fees-board-cost fees-expense', ';padding:4px 7px;font-weight:700'));
      // Остатъкът е единственото число тук, което може да е ОТРИЦАТЕЛНО —
      // затова минава през profitCell (символ + дума, не само цвят).
      cells.push(r.left == null
        ? centsCell(null, 'fees-board-left', ';padding:4px 7px')
        : addClass(profitCell(r.left), 'fees-board-left'));

      // Редът се отваря и с КЛАВИАТУРА: clickableRow(), не onClick на <tr>.
      // Точно този капан веднъж направи цял списък недостъпен (виж CLAUDE.md).
      const tr = clickableRow(
        () => accountantClientsDialog(r, ctx.counts),
        'Покажи фирмите на ' + (r.name || 'клиентите без счетоводител'),
        ...cells);
      tr.setAttribute('data-name', label);
      tr.setAttribute('data-acc-key', r.key);
      tr.setAttribute('title', 'Натиснете реда, за да видите фирмите му и таксите им.');
      tb.appendChild(tr);
    });

    // ЗАЩО „Остава" в подвала НЕ е сборът на клетките: ред с липсващо начислено
    // показва „—", но разходът му ВЛИЗА в общия разход. Сборът на видимите
    // остатъци щеше тихо да изяде точно този разход. Затова редът „Общо" следва
    // СЪЩОТО правило като клетките — общо начислено минус общ разход.
    const totalLeft = (sums.expected == null || sums.cost == null) ? null : sums.expected - sums.cost;
    const tfoot = el('tfoot', { style: 'border-top:2px solid var(--border)' }, el('tr', { class: 'fees-sum' },
      el('th', { scope: 'row', style: TD + ';font-weight:700;text-align:left', text: 'Общо' }),
      countCell(sums.clients, 'fees-board-clients'),
      centsCell(sums.expected, 'fees-board-expected', ';padding:4px 7px;font-weight:700'),
      centsCell(sums.direct, 'fees-board-direct', ';padding:4px 7px;font-weight:700'),
      centsCell(sums.shared, 'fees-board-shared', ';padding:4px 7px;font-weight:700'),
      centsCell(sums.cost, 'fees-board-cost', ';padding:4px 7px;font-weight:700'),
      totalLeft == null
        ? centsCell(null, 'fees-board-left', ';padding:4px 7px;font-weight:700')
        : addClass(profitCell(totalLeft), 'fees-board-left')));

    box.appendChild(el('table', { class: 'fees-board-table' },
      el('thead', {}, el('tr', {}, ...BOARD_COLS.map(head))), tb, tfoot));

    // Сборовете ТРЯБВА да съвпадат с общите числа на кантората. Разминат ли се,
    // това е грешка в справката, не в екрана — и по-добре екранът да го КАЖЕ,
    // отколкото собственикът да го открие с калкулатор (точно оплакването му
    // „колоните не се равняват").
    //
    // Сверката на ПЛАТЕНОТО отпадна заедно с колоната. На нейно място стои
    // НАЧИСЛЕНОТО: справката по месеци може да носи човек, за когото няма ред в
    // „принос" — тогава той изобщо не стига до таблото и начисленото му увисва
    // невидимо. Това не е тавтология: двата сбора идват от различни списъци.
    const off = [];
    if (hasMonthly && sums.expected != null && totalExpected !== sums.expected) {
      off.push('„Начислено" дава ' + eur(sums.expected) + ', а общото начислено е ' + eur(totalExpected));
    }
    if (ctx.totalExpense != null && sums.cost != null && Number(ctx.totalExpense) !== sums.cost) {
      off.push('„Разход общо" дава ' + eur(sums.cost) + ', а общият разход е ' + eur(ctx.totalExpense));
    }
    if (off.length) {
      foot.appendChild(el('div', {
        class: 'cal-hint fees-board-mismatch',
        style: 'color:var(--red);font-weight:600;white-space:normal',
      },
        el('span', { 'aria-hidden': 'true', text: '⚠ ' }),
        'Колоните не се равняват: ' + off.join('; ') + '.'));
    }
    foot.appendChild(el('div', {
      class: 'cal-hint',
      text: 'Един ред на счетоводител. „Начислено" е колко му начисляваме, „Остава" е начисленото минус общия разход. '
        + 'Натиснете ред, за да видите неговите фирми с таксите им. Заглавията се натискат за подредба. '
        + 'Редът „Общо" смята „Остава" по същото правило — общо начислено минус общ разход; затова може да се различава '
        + 'от сбора на клетките, когато при някого начисленото липсва.',
    }));

    // Пребоядисването пресъздава бутона, който току-що е натиснат — фокусът
    // щеше да падне на <body>. Връщаме го там, където беше (същият похват като
    // при „Колони").
    if (focusCol) {
      const again = box.querySelector('button[data-sort="' + focusCol + '"]');
      if (again) again.focus();
    }
  }

  paint(null);
  return panel;
}

// Добавя клас, без да губи вече сложените (profitCell идва със свои).
function addClass(node, cls) { node.className = node.className + ' ' + cls; return node; }

// ---- „Кои фирми осчетоводява" ----------------------------------------------
//
// Искане на собственика: „като натисна счетоводителя в таблото да ми изкарва
// фирмите, които осчетоводява, с таксите, които носи".
//
// ЗАЩО без нова заявка: списъкът на матрицата вече е в паметта (_clientsCache)
// и точно от него се брои колоната „Клиенти". Второ дърпане на същия адрес би
// било не само загуба, а и риск двата списъка да се разминат — таблото да пише
// „3 клиента", а диалогът да показва други две фирми.
//
// ЗАЩО сдвояването минава през clientsFor(): едно правило за брояча и за
// списъка. Разделени, рано или късно щяха да отговарят на различни въпроси.
function accountantClientsDialog(row, counts) {
  const who = row.name || 'клиентите без счетоводител';
  const title = 'Фирми на ' + (row.name || UNALLOCATED) + ' — ' + _year + ' г.';
  const found = clientsFor(counts, row);

  // Списъкът още не е зареден (смяна на година върху „Анализи") — това е ДРУГО
  // от „няма фирми" и се казва с други думи, за да не тръгне човек да търси
  // изчезнали клиенти.
  if (!found) {
    return openModal({
      title,
      body: el('p', { style: 'margin:0', text: 'Списъкът с клиенти за ' + _year + ' г. още не е зареден. '
        + 'Отворете таб „Такси" веднъж и се върнете тук.' }),
      foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal })),
    });
  }

  if (!found.length) {
    return openModal({
      title,
      body: el('p', { style: 'margin:0' }, 'На името „' + (row.name || UNALLOCATED) + '" няма нито една фирма в „Такси" за '
        + _year + ' г. Ако човекът работи фирми, проверете как е изписан в колоната „Работи го" — '
        + 'сдвояването е по номер на потребител, а когато номер няма, по изписано име.'),
      foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal })),
    });
  }

  // Най-начисленото отгоре: въпросът е „кои фирми носят най-много".
  const items = found.map((c) => c.src || {}).slice()
    .sort((a, b) => (Number(b.expected_cents) || 0) - (Number(a.expected_cents) || 0)
      || String(a.label || '').localeCompare(String(b.label || ''), 'bg'));

  const sums = { base: 0, active: 0, expected: 0, paid: 0, debt: 0 };
  const num = (v, cls, bold) => el('td', {
    class: 'num fees-cl-' + cls,
    style: 'white-space:nowrap;text-align:right' + (bold ? ';font-weight:700' : ''),
    text: eur(v),
  });

  const tb = el('tbody');
  items.forEach((c) => {
    sums.base += Number(c.fee_base_cents) || 0;
    sums.active += Number(c.fee_active_cents) || 0;
    sums.expected += Number(c.expected_cents) || 0;
    sums.paid += Number(c.paid_cents) || 0;
    sums.debt += Number(c.debt_cents) || 0;
    const owes = Number(c.debt_cents) > 0;
    tb.appendChild(el('tr', { 'data-client-id': String(c.id) },
      el('td', { style: 'white-space:normal' },
        el('div', { text: c.label || ('№ ' + c.id) }),
        // Годишното приключване е ЕДНОКРАТНА такса — без този надпис 300,00 €
        // до месечните такси се чете като месечна.
        c.kind === 'annual' ? el('div', {
          style: 'font-size:12px;color:var(--muted)', text: 'годишно приключване (еднократна такса)',
        }) : null,
        periodLabel(c) ? el('div', { style: 'font-size:12px;color:var(--muted)', text: periodLabel(c) }) : null),
      num(c.fee_base_cents, 'fee-base'),
      num(c.fee_active_cents, 'fee-active'),
      num(c.expected_cents, 'expected'),
      num(c.paid_cents, 'paid'),
      el('td', {
        class: 'num fees-cl-debt' + (owes ? ' owes' : ''),
        style: 'white-space:nowrap;text-align:right;font-weight:700;'
          + (owes ? 'color:var(--red)' : 'color:var(--muted)'),
        text: eur(c.debt_cents),
      })));
  });

  const table = el('table', { class: 'fees-acc-clients' },
    el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Фирма' }),
      el('th', { scope: 'col', class: 'num', title: 'Основна месечна такса (в стария ексел: D)', text: 'Основна такса' }),
      el('th', { scope: 'col', class: 'num', title: 'Такса за месеците с активна ДДС дейност (в стария ексел: E)', text: 'ДДС такса' }),
      el('th', { scope: 'col', class: 'num', text: 'Начислено' }),
      el('th', { scope: 'col', class: 'num', text: 'Платено' }),
      el('th', { scope: 'col', class: 'num', text: 'Дълг' }))),
    tb,
    el('tfoot', { style: 'border-top:2px solid var(--border)' }, el('tr', { class: 'fees-sum' },
      el('th', { scope: 'row', style: 'font-weight:700;text-align:left', text: 'Общо (' + items.length + ')' }),
      // Таксите D и E са месечни СТАВКИ — сборът им е „колко на месец", а не
      // приход. Казва се в подсказката, за да не се чете като начислено.
      addClass(num(sums.base, 'fee-base', true), 'fees-cl-rate-sum'),
      addClass(num(sums.active, 'fee-active', true), 'fees-cl-rate-sum'),
      num(sums.expected, 'expected', true),
      num(sums.paid, 'paid', true),
      num(sums.debt, 'debt', true))));
  [...table.querySelectorAll('tfoot .fees-cl-rate-sum')].forEach((td) => td.setAttribute('title',
    'Сбор на месечните ставки. ДДС таксата се дължи само в месеците с активна ДДС дейност — сборът не е начислено.'));

  return openModal({
    title, wide: true,
    body: el('div', {},
      el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px',
        text: 'Фирмите, които работи ' + who + ', с таксите им за ' + _year + ' г. Числата са същите като в таб „Такси".' }),
      table),
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal })),
  });
}

// ---- Списък на разходите + форма ------------------------------------------

// Чий е разходът. Вързан ли е за СЛУЖЕБЕН потребител, сървърът дава готово име
// от профила (accountant_name) — то е истината и не може да се разпише грешно.
// Заварен разход има само свободния текст и се показва както си е.
const personOf = (x) => String((x && (x.accountant_name || x.person)) || '').trim();

function expensesListPanel(items, list, wrap, staff) {
  const panel = el('div', { class: 'panel fees-expenses-list' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Разходи — ' + _year }),
    el('span', { style: 'color:var(--muted);font-size:13px;margin-left:auto', text: items.length + ' записа' }),
    el('button', {
      class: 'btn sm fees-expense-add', type: 'button', text: '+ Нов разход',
      onClick: () => expenseForm(wrap, null, list, staff),
    })));
  if (!items.length) {
    panel.appendChild(emptyState('€', 'Още няма записани разходи за ' + _year + ' г.', '+ Нов разход',
      () => expenseForm(wrap, null, list, staff)));
    return panel;
  }
  const tb = el('tbody');
  // Най-новият месец отгоре: последното въведено се проверява най-често.
  items.slice().sort((a, b) => (Number(b.month) || 0) - (Number(a.month) || 0) || (Number(b.id) || 0) - (Number(a.id) || 0))
    .forEach((x) => tb.appendChild(expenseRow(x, list, wrap, staff)));
  panel.appendChild(el('table', {},
    el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Месец' }),
      el('th', { scope: 'col', text: 'Категория' }),
      el('th', { scope: 'col', text: 'Човек' }),
      el('th', { scope: 'col', class: 'num', text: 'Сума' }),
      el('th', { scope: 'col', text: 'Бележка' }),
      el('th', { scope: 'col' }, el('span', { class: 'sr-only', text: 'Действия' })))), tb));
  return panel;
}

// Редът НЕ е кликаем през <tr> (правилото на проекта) — редакцията и
// изтриването са бутони със собствени имена.
function expenseRow(x, list, wrap, staff) {
  const m = monthOrNull(x.month);
  const when = m ? MONTHS_FULL[m - 1] : '—';
  const label = catLabel(list, x.category);
  const who = personOf(x);
  const what = when + ' · ' + label + (who ? ' · ' + who : '') + ' · ' + eur(x.amount_cents);
  return el('tr', { 'data-expense-id': String(x.id) },
    el('td', { style: 'white-space:nowrap', text: when }),
    el('td', { text: label }),
    el('td', { text: who || '—' }),
    el('td', { class: 'num', style: 'white-space:nowrap;font-weight:700', text: eur(x.amount_cents) }),
    el('td', { text: x.note || '' }),
    el('td', { style: 'text-align:right;white-space:nowrap' },
      el('button', {
        class: 'btn sm ghost fees-expense-edit', type: 'button', text: '✎',
        title: 'Редакция на разхода', 'aria-label': 'Редакция на разхода: ' + what,
        onClick: () => expenseForm(wrap, x, list, staff),
      }),
      el('button', {
        class: 'btn sm ghost fees-expense-del', type: 'button', text: '×',
        title: 'Изтрий разхода', 'aria-label': 'Изтрий разхода: ' + what,
        onClick: () => removeExpense(x, list, wrap),
      })));
}

async function removeExpense(x, list, wrap) {
  const m = monthOrNull(x.month);
  const ok = await confirmDialog(
    'Да изтрия ли разхода „' + catLabel(list, x.category) + '"'
    + (m ? ' за ' + MONTHS_FULL[m - 1] : '') + ' (' + eur(x.amount_cents) + ')? Печалбата се преизчислява веднага.',
    { okText: 'Изтрий разхода', danger: true }
  );
  if (!ok) return;
  try {
    await api('/fees/expenses/' + x.id, { method: 'DELETE' });
    toast('Разходът е изтрит.');
    draw(wrap);
  } catch (e) { toast(e.message, true); }
}

// ---- Кой е човекът на разхода ----------------------------------------------
//
// ЗАЩО падащо меню, а не свободен текст: докато полето беше текст, „Иван Петров"
// и „Иван петров" бяха двама различни души и приносът на счетоводителя се
// разпадаше на два реда, без никой да разбере защо. Затова човекът се избира от
// СЛУЖЕБНИТЕ потребители и разходът пътува с НОМЕР (accountant_user_id).
//
// „(друг — впиши име)" е СТОЙНОСТ на менюто, не празно: празното значи „никой"
// (наем, банкови такси), а „друг" значи външен човек — чистачка, куриер, — на
// когото няма профил и името му няма как да се сдвои.
const OTHER_PERSON = '__other__';

// Списъкът от /api/fees/accountants. Връща null, когато адресът го НЯМА (стар
// сървър) или е празен — тогава остава старото свободно поле, вместо меню без
// нито един избор. Празни и повредени редове се изхвърлят: опция без име не
// казва нищо, а опция без номер не може да се запише.
function accountantsOf(raw) {
  const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.accountants) ? raw.accountants : null);
  if (!list) return null;
  const out = list
    .map((u) => ({ id: Number(u && u.id) || null, name: String((u && u.name) || '').trim() }))
    .filter((u) => u.id && u.name);
  return out.length ? out : null;
}

function expenseForm(wrap, existing, list, staff) {
  const it = existing || {};
  const now = new Date();
  // Нов разход тръгва от ТЕКУЩИЯ месец (най-честият случай), но само ако гледаме
  // текущата година — иначе януари.
  const curMonth = monthOrNull(it.month) || (_year === now.getFullYear() ? now.getMonth() + 1 : 1);
  const monthSel = el('select', { class: 'fees-expense-month' },
    ...MONTHS_FULL.map((n, i) => el('option', { value: i + 1, text: n, selected: curMonth === i + 1 })));

  const cats = (list && list.length) ? list : categoriesOf(null, null);
  const curCat = it.category || cats[0].key;
  const catSel = el('select', { class: 'fees-expense-category' },
    ...cats.map((c) => el('option', { value: c.key, text: c.label, selected: c.key === curCat })));

  const personEl = input({ class: 'fees-expense-person', type: 'text', value: it.person || '', placeholder: 'име на човека' });
  const amountEl = input({
    class: 'fees-expense-amount', type: 'text', inputmode: 'decimal',
    value: fromCents(it.amount_cents), placeholder: '0,00',
  });
  const noteEl = el('textarea', { rows: 2, class: 'fees-expense-note', placeholder: 'Бележка (по избор)' }, it.note || '');

  // Има ли списък със служебни потребители → менюто носи „кой"; няма ли (стар
  // сървър) → носи го старото текстово поле, точно както досега.
  const staffList = Array.isArray(staff) && staff.length ? staff : null;
  const curId = it.accountant_user_id == null || it.accountant_user_id === '' ? null : Number(it.accountant_user_id);
  const personSel = staffList ? el('select', { class: 'fees-expense-person-select' },
    el('option', { value: '', text: '(без човек)' }),
    ...staffList.map((u) => el('option', { value: String(u.id), text: u.name })),
    el('option', { value: OTHER_PERSON, text: '(друг — впиши име)' })) : null;

  if (staffList) {
    if (curId && staffList.some((u) => u.id === curId)) personSel.value = String(curId);
    else if (curId) {
      // ЗАВАРЕН разход на човек ИЗВЪН списъка (напуснал, скрит профил): пази се
      // като изрична, избрана опция. Иначе браузърът тихо избира първата и
      // „Запази" преписва разхода на друг — същият капан като в бланката.
      // Името: първо от профила, после последното записано, чак накрая номерът —
      // безименна опция не казва на кого е разходът.
      const legacyName = String(it.accountant_name || '').trim()
        || String(it.person || '').trim()
        || ('потребител №' + curId);
      personSel.appendChild(el('option', {
        value: String(curId), selected: true, text: legacyName + ' (заварен)',
      }));
      personSel.value = String(curId);
    } else if (String(it.person || '').trim()) personSel.value = OTHER_PERSON;
    else personSel.value = '';
  }

  const whoEl = staffList ? personSel : personEl;
  const whoField = field('Човек', whoEl);
  const whoLabel = whoField.querySelector('label');
  // Свободното поле има СВОЙ надпис и излиза само при „друг" — две полета за
  // едно и също, показани заедно, оставят човека да гадае кое важи.
  const otherField = staffList ? field('Име на човека (външен)', personEl) : null;
  const personHint = el('div', { class: 'cal-hint', style: 'padding:0 4px 8px' });

  // Полето „човек" значи нещо само при заплати и осигуровки — сървърът казва за
  // кои категории (`person: true`). При тях се ИСКА: без човек разбивката „по
  // хора" остава празна и никой не разбира защо.
  const needsPerson = () => !!(catOf(cats, catSel.value) || {}).person;
  const isOther = () => !!staffList && personSel.value === OTHER_PERSON;
  const syncOther = () => {
    if (!otherField) return;
    otherField.setAttribute('style', isOther() ? '' : 'display:none');
  };
  const syncPerson = () => {
    const on = needsPerson();
    whoLabel.textContent = on ? 'Човек (на кого — задължително)' : 'Човек (по избор)';
    if (on) whoEl.setAttribute('aria-required', 'true');
    else whoEl.removeAttribute('aria-required');
    personHint.textContent = on
      ? (staffList
        ? 'При заплати и осигуровки изберете човека от списъка — така разходът се вързва за него по НОМЕР и приносът му се смята вярно. За външен човек изберете „(друг — впиши име)".'
        : 'При заплати и осигуровки напишете на кого са — иначе разбивката „по хора" остава празна.')
      : 'За тази категория човек не е нужен.';
    syncOther();
  };
  catSel.addEventListener('change', syncPerson);
  if (personSel) personSel.addEventListener('change', syncOther);
  syncPerson();

  const save = async () => {
    const cents = toCents(amountEl.value);
    if (cents === null) { toast('Въведете сума с цифри, например 1500 или 1500,50.', true); amountEl.focus(); return; }
    if (!cents) { toast('Разход с нула не променя нищо — въведете сумата.', true); amountEl.focus(); return; }
    // Едно от двете, никога и двете: или НОМЕР на служебен потребител (сигурно
    // сдвояване), или свободно име на външен човек.
    let person = '';
    let accId = null;
    if (staffList) {
      if (isOther()) person = personEl.value.trim();
      else accId = Number(personSel.value) || null;
    } else {
      person = personEl.value.trim();
    }
    if (needsPerson() && !accId && !person) {
      toast(staffList
        ? 'Изберете на кого е разходът — без човек разбивката „по хора" остава празна.'
        : 'Напишете на кого е — без име разбивката „по хора" остава празна.', true);
      (isOther() ? personEl : whoEl).focus();
      return;
    }
    const payload = {
      year: _year,
      month: monthOrNull(monthSel.value) || 1,
      category: catSel.value,
      person,
      amount_cents: cents,
      note: noteEl.value.trim(),
    };
    // Ключът тръгва САМО когато има откъде да се избере човек: стар сървър, който
    // не го познава, не бива да получава поле, за което не е питал.
    if (staffList) payload.accountant_user_id = accId;
    try {
      if (it.id) await api('/fees/expenses/' + it.id, { method: 'PUT', body: JSON.stringify(payload) });
      else await api('/fees/expenses', { method: 'POST', body: JSON.stringify(payload) });
      closeModal();
      toast('Записано.');
      draw(wrap);
    } catch (e) { toast(e.message, true); }
  };

  // openModal иска ОБЕКТ ({ title, body, foot }) — с два аргумента диалогът
  // мълчи. Този капан вече се е случвал в този файл.
  openModal({
    title: it.id ? 'Разход' : 'Нов разход',
    body: el('div', {},
      el('div', { class: 'field-row' },
        field('Месец', monthSel, { size: 'm' }),
        field('Категория', catSel, { size: 'm' })),
      whoField,
      otherField,
      personHint,
      el('div', { class: 'field-row' },
        field('Сума', amountEl, { size: 's' })),
      field('Бележка', noteEl)),
    foot: el('div', {},
      el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', type: 'button', text: 'Запази', style: 'margin-left:8px', onClick: save })),
  });
}

async function drawAnalytics(host, keep, wrap) {
  // Редовете на разходите (с id — за редакция и изтриване), списъкът с
  // категории и служебните потребители идват от ОТДЕЛНИ адреси. Заявките са
  // независими и вървят ЕДНОВРЕМЕННО — иначе екранът чака три пъти по ред. Стар
  // сървър, който още не ги знае, не бива да събаря останалите справки: затова
  // .catch(null) и по-надолу всяка част се показва само ако има данни.
  const pending = api('/fees/expenses?year=' + _year).catch(() => null);
  const pendingStaff = api('/fees/accountants').catch(() => null);
  // Броят клиенти на всеки счетоводител се смята от списъка на матрицата. Той
  // почти винаги е ВЕЧЕ зареден (екранът тръгва от таб „Такси") — тогава тук не
  // се прави никаква заявка. Дърпа се само когато списъкът е за друга година,
  // тоест при смяна на годината върху „Анализи": брой от миналата година до
  // тазгодишни суми е по-лош от липсващ брой.
  const pendingClients = cachedClients(_year) ? null : api('/fees?year=' + _year).catch(() => null);
  let a;
  try { a = await api('/fees/analytics?year=' + _year); }
  catch (e) { clear(host); host.appendChild(el('div', { class: 'cal-hint', text: e.message })); return; }
  const ex = await pending;
  const staff = accountantsOf(await pendingStaff);
  if (pendingClients) {
    const fresh = await pendingClients;
    if (fresh && Array.isArray(fresh.clients)) rememberClients(_year, fresh.clients);
  }
  clear(host);

  const profit = a.profit || null;
  const agg = a.expenses || ex || null;
  const items = (ex && Array.isArray(ex.expenses)) ? ex.expenses : null;
  const cats = categoriesOf(ex, (agg && agg.by_category) || []);
  // Докъде са начисленията и разходите (7 = до юли включително). Едно място за
  // целия екран: надписът горе, таблото, разходите по хора и Excel стъпват на
  // него, за да не могат да покажат различни периоди.
  const through = monthOrNull(a.through_month);
  // Месецът за „Разходи по хора" се решава ВЕДНЪЖ тук: и панелът, и Excel-ът
  // питат едно и също число, вместо всеки да си избира по подразбиране.
  if (!_personMonth) _personMonth = through || 1;
  // Общият разход на кантората — ЕДНО число за целия екран. И таблото, и
  // разходите по хора сверяват сборовете си срещу него; две отделни сметки биха
  // могли да покажат две различни „истини" на един и същ екран.
  const totalExpense = (agg && agg.total_cents != null)
    ? agg.total_cents
    : (profit ? profit.total_expense_cents : null);

  // ---- Подтабовете ----
  //
  // Съдържанието се пречертава от ВЕЧЕ заредените данни: превключването между
  // групи справки не бива да пита сървъра пак (същите числа, само други панели),
  // а самите бутони остават едни и същи възли, за да не изчезне фокусът изпод
  // пръста на човека, който работи с клавиатура.
  const panels = el('div', { class: 'fees-ana-panels' });
  const btns = {};
  const group = el('div', {
    class: 'fees-ana-tabs', role: 'group', 'aria-label': 'Кои справки да се показват',
    style: 'display:flex;gap:6px;flex-wrap:wrap',
  }, ...ANA_TABS.map((t) => {
    const b = el('button', {
      type: 'button', 'data-ana-tab': t.id, title: t.hint, text: t.label,
      onClick: () => { if (_anaTab === t.id) return; _anaTab = t.id; saveAnaTab(); paint(); },
    });
    btns[t.id] = b;
    return b;
  }));

  // Периодът стои НАД всичко: той важи за всяка справка отдолу, а не за една.
  host.appendChild(throughNote(through));
  host.appendChild(el('div', { class: 'filter-bar fees-ana-bar' },
    group,
    el('button', {
      class: 'btn sm ghost fees-xlsx-analytics', type: 'button', text: '⤓ Excel',
      style: 'margin-left:auto',
      title: 'Сваля справките като Excel файлове.',
      'aria-label': 'Excel — сваля справките за ' + _year + ' г.',
      onClick: () => analyticsXlsxDialog({
        profit, items, cats, acc: a.by_accountant_monthly, through,
        contribution: Array.isArray(a.accountant_contribution) ? a.accountant_contribution : null,
        counts: clientCounts(cachedClients(_year)),
        persons: (agg && agg.by_person) || null,
      }),
    })));
  host.appendChild(panels);

  // Парите на кантората — първият въпрос на екрана, затова и първият подтаб.
  function moneyPanels() {
    const out = [];
    if (profit) {
      const income = statCard('Приход', eur(profit.total_income_cents), 'постъпило за ' + _year + ' г.');
      income.classList.add('fees-card-income');
      const expense = statCard('Разход', eur(profit.total_expense_cents), 'заплати, осигуровки и други за ' + _year + ' г.');
      expense.classList.add('fees-card-expense');
      out.push(el('div', { class: 'cards fees-money-cards' }, income, expense, profitCard(profit.total_profit_cents)));
      out.push(profitMonthsPanel(profit));
    } else {
      // Без данни НЕ се измисля печалба — казва се защо я няма.
      out.push(el('div', {
        class: 'cal-hint fees-expenses-missing',
        text: 'Разходите и печалбата още не идват от сървъра — тази част ще се появи след обновяване на системата.',
      }));
    }
    if (agg) out.push(categoryPanel(cats, agg.by_category || []));
    // „Общ месечен приход" е СЪЩИЯТ масив, от който сървърът смята колоната
    // „Приход" в таблицата по месеци (monthly_income ↔ profit.by_month). Две
    // таблици с едни и същи дванайсет числа само удължават екрана — точно това,
    // заради което разделяме на подтабове. Затова панелът излиза САМО когато
    // печалбата липсва (стар сървър): тогава той е единственият носител на
    // месечния приход и не бива да изчезва.
    if (!profit) {
      out.push(listPanel('Общ месечен приход', 'fees-monthly-income', 'Месец', 'Приход',
        (a.monthly_income || []).map((r) => ({
          name: MONTHS_FULL[Number(r.month) - 1] || String(r.month || ''),
          amount: r.paid_cents,
        }))));
    }
    // Списъкът с разходите стои при парите: там се въвеждат и там се проверяват.
    if (items) out.push(expensesListPanel(items, cats, wrap, staff));
    return out;
  }

  function clientsPanels() {
    return [
      listPanel('Кой дължи', 'fees-debtors', 'Клиент', 'Дълг',
        (a.debtors || []).map((r) => ({ name: nameOf(r), amount: debtOf(r) }))),
      listPanel('Кой е надплатил', 'fees-overpaid', 'Клиент', 'Надплатено',
        (a.overpaid || []).map((r) => ({ name: nameOf(r), amount: overOf(r) }))),
      twoColumnPanel('Дълг и приход по подпис (БВ / ДВ)', 'Подпис', 'fees-by-signature',
        (a.by_signature || []).map((r) => ({ name: r.signature || '—', debt: r.debt_cents, paid: r.paid_cents }))),
    ];
  }

  function accountantsPanels() {
    return [
      // „Дълг и приход по счетоводител" го НЯМА нарочно: собственикът каза, че
      // не върши работа. Таблото отговаря на същия въпрос и на още четири.
      accountantsBoardPanel({
        contribution: Array.isArray(a.accountant_contribution) ? a.accountant_contribution : null,
        monthly: Array.isArray(a.by_accountant_monthly) ? a.by_accountant_monthly : [],
        counts: clientCounts(cachedClients(_year)),
        // Общият разход на кантората — таблото сверява сбора си срещу него.
        // Общият ПРИХОД вече не влиза: колоната „Платено" отпадна, а начисленото
        // се сверява срещу справката по месеци (виж accountantsBoardPanel).
        totalExpense,
      }),
      accountantMonthsPanel(Array.isArray(a.by_accountant_monthly) ? a.by_accountant_monthly : []),
      // Разходите по хора стоят ТУК, до таблото: двете отговарят на един въпрос
      // („колко ми струва този човек"), а разделени се сравняват на ум. Общият
      // разход пътува и дотук — сборът на колоната „Общо" трябва да е равен
      // точно на него, откакто споделеното влиза в таблицата.
      agg ? personPanel(agg.by_person || [], through, totalExpense) : null,
    ];
  }

  function paint() {
    ANA_TABS.forEach((t) => {
      const on = _anaTab === t.id;
      btns[t.id].className = 'btn sm' + (on ? '' : ' ghost');
      btns[t.id].setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    clear(panels);
    const kids = _anaTab === 'clients' ? clientsPanels()
      : (_anaTab === 'accountants' ? accountantsPanels() : moneyPanels());
    kids.forEach((n) => { if (n) panels.appendChild(n); });
  }

  paint();

  // Широката справка също не бива да скача най-отляво при пре-render.
  if (keep) restoreScroll(host, keep);
}

// ---- Excel в „Анализи" -----------------------------------------------------
//
// ЗАЩО диалог, а не един бутон: общият помощник прави ЕДИН лист във файл (виж
// src/fees/xlsx.js), тоест трите справки не могат да влязат в един файл. Тук се
// избира коя се сваля — и на екрана пише защо са отделни, за да не изглежда, че
// останалите са се загубили.
function analyticsXlsxDialog(ctx) {
  const one = (label, hint, cls, onClick) => el('div', { style: 'margin-bottom:10px' },
    el('button', { class: 'btn sm ' + cls, type: 'button', text: '⤓ ' + label, onClick }),
    el('div', { class: 'cal-hint', style: 'padding:2px 0 0', text: hint }));

  const expensesModel = () => ({
    year: _year,
    rows: (ctx.items || []).slice()
      .sort((x, y) => (Number(x.month) || 0) - (Number(y.month) || 0) || (Number(x.id) || 0) - (Number(y.id) || 0))
      .map((x) => ({
        month: MONTHS_FULL[(monthOrNull(x.month) || 1) - 1],
        category: catLabel(ctx.cats, x.category),
        person: personOf(x),
        amount_cents: x.amount_cents,
        note: x.note || '',
      })),
  });

  const profitModel = () => ({
    year: _year,
    rows: (((ctx.profit || {}).by_month) || []).map((r) => ({
      month: MONTHS_FULL[(monthOrNull(r.month) || 1) - 1],
      income_cents: r.income_cents,
      expense_cents: r.expense_cents,
      profit_cents: r.profit_cents,
    })),
  });

  // Изнася се ИЗБРАНОТО на екрана (платено или начислено) — файлът не бива да
  // показва друго число от справката, от която е свален.
  const accModel = () => {
    const paid = _accMode !== 'expected';
    return {
      year: _year,
      mode: _accMode,
      monthLabels: MONTHS_SHORT,
      rows: (ctx.acc || []).map((r) => ({
        name: String((r && r.name) || '').trim() || '(без счетоводител)',
        months: MONTHS_SHORT.map((x, i) => {
          const info = (r.months || []).find((z) => Number(z.month) === i + 1) || {};
          return Number(paid ? info.paid_cents : info.expected_cents) || 0;
        }),
        total_cents: Number(paid ? r.total_paid_cents : r.total_expected_cents) || 0,
      })),
    };
  };

  // Периодът влиза в САМИЯ лист, не само в името на файла: изнесеният файл
  // тръгва по хората сам и „Разход 2 100" без месец лъже толкова, колкото и на
  // екрана. Затова е първа колона на всеки ред.
  const periodLabelOf = () => (ctx.through ? 'до ' + MONTHS_FULL[ctx.through - 1] + ' ' + _year : String(_year));

  // Таблото се изнася ПОДРЕДЕНО както е на екрана — файлът повтаря екрана,
  // вместо да е втора, различна справка (същото правило като при матрицата).
  const boardModel = () => ({
    year: _year,
    period: periodLabelOf(),
    rows: sortBoardRows(boardRows(ctx.contribution, ctx.acc || [], ctx.counts), _accSort).map((r) => ({
      name: r.name || UNALLOCATED,
      clients: r.clients,
      expected_cents: r.expected,
      direct_cost_cents: r.direct,
      shared_cost_cents: r.shared,
      cost_cents: r.cost,
      profit_cents: r.left,
      // Нулата на несдвоен човек значи „няма клиенти на това име", не „не носи
      // нищо" — на екрана го пише с думи, в листа също.
      note: r.matched ? '' : 'няма клиенти на това име',
    })),
  });

  // Разходите по хора следват ИЗБОРА на екрана (месец или година) — иначе
  // файлът показва друго число от справката, от която е свален.
  const personsModel = () => {
    const month = _personScope === 'month' ? (monthOrNull(_personMonth) || ctx.through || 1) : null;
    const period = month ? MONTHS_FULL[month - 1] + ' ' + _year : periodLabelOf();
    return {
      year: _year,
      month,
      period,
      // Колоната „Споделени" влиза в листа само когато я има и на екрана —
      // СЪЩАТА проверка (hasSharedCost), не втора своя. Колона с празни клетки
      // се чете като „на този човек не се пада нищо общо".
      shared: hasSharedCost(ctx.persons),
      rows: (ctx.persons || []).map((r) => {
        const src = month
          ? ((Array.isArray(r.months) ? r.months : []).find((x) => Number(x && x.month) === month) || {})
          : r;
        return {
          person: personOf(r) || '(без човек)',
          salary_cents: month ? (Number(src.salary_cents) || 0) : cent(src.salary_cents),
          insurance_cents: month ? (Number(src.insurance_cents) || 0) : cent(src.insurance_cents),
          other_cents: month ? (Number(src.other_cents) || 0) : cent(src.other_cents),
          shared_cents: month ? (Number(src.shared_cents) || 0) : cent(src.shared_cents),
          amount_cents: Number(src.amount_cents) || 0,
        };
      }),
    };
  };

  // openModal иска ОБЕКТ ({ title, body, foot }) — с два аргумента диалогът
  // мълчи. Този капан вече се е случвал в този файл.
  openModal({
    title: 'Excel — коя справка',
    body: el('div', {},
      el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px', text: 'Всяка справка се сваля в отделен файл (помощникът за Excel слага един лист във файл). Сумите са ЧИСЛА в евро — събират се направо в Excel.' }),
      one('Табло на счетоводителите',
        'Клиенти, начислено, пряк и споделен разход и остатъкът (начислено минус разход) — както са подредени на екрана.',
        'fees-xlsx-board', () => exportAccountantsBoard(boardModel())),
      one('Разходи по хора',
        'Заплати, осигуровки, други и споделеният дял от общите разходи — според избора „за месец / за годината".',
        'fees-xlsx-persons', () => exportExpensesByPerson(personsModel())),
      one('Разходи', 'Месец, категория, човек, сума и бележка.', 'fees-xlsx-expenses',
        () => exportExpenses(expensesModel())),
      one('Печалба по месеци', 'Приход, разход и печалба за всеки месец.', 'fees-xlsx-profit',
        () => exportProfit(profitModel())),
      one('Приход по счетоводител и месец',
        'Дванайсетте месеца и общото — според избора „платено/начислено".', 'fees-xlsx-accountants',
        () => exportAccountantMonths(accModel()))),
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal })),
  });
}

// ---- Внос от файл ---------------------------------------------------------
//
// Историята на модула се подготвя извън системата (екселс → JSON) и се внася
// оттук, а не през терминал. Файлът се чете В БРАУЗЪРА, точно както вносът на
// работната бланка — сървърът получава вече готовото съдържание.
//
// Двустъпково НАРОЧНО: „Прегледай" НЕ пише нищо и показва какво е разчетено,
// кои фирми не се намират в чек листата и къде подписът се разминава. Едва
// след това „Внеси" записва. Повторен внос при вече заредени клиенти получава
// 409 от сървъра — за да не се удвоят данните.
function importForm(wrap) {
  const file = el('input', { type: 'file', accept: '.json,application/json' });
  const info = el('div', { class: 'cal-hint', text: 'Изберете JSON файла с историята (подготвен от екселса).' });
  const result = el('div', {});
  let payload = null;

  file.addEventListener('change', () => {
    const f = file.files && file.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        payload = JSON.parse(String(reader.result));
        const n = Array.isArray(payload.clients) ? payload.clients.length : 0;
        // Мярката на сумите е задължителна и се показва: гадаене между евро и
        // центове е стократна грешка, затова се вижда преди да се натисне нещо.
        info.textContent = 'Файл: ' + f.name + ' — ' + n + ' реда, суми в „' + (payload.amounts || '?') + '". Натиснете „Прегледай".';
      } catch (e) {
        payload = null;
        info.textContent = 'Файлът не е валиден JSON: ' + e.message;
      }
    };
    reader.readAsText(f, 'utf-8');
  });

  const send = async (mode) => {
    if (!payload) { toast('Първо изберете файл.', true); return; }
    clear(result);
    result.appendChild(el('div', { class: 'cal-hint', text: 'Обработвам…' }));
    try {
      const body = Object.assign({}, payload, { mode });
      const p = await api('/fees/import', { method: 'POST', body: JSON.stringify(body) });
      clear(result);
      const line = (t, strong) => result.appendChild(el('div', strong ? { style: 'font-weight:700;margin-bottom:6px' } : {}, t));
      if (mode === 'preview') {
        line('Ще се внесат: ' + (p.clients || 0) + ' клиента, ' + (p.payments || 0) + ' плащания, '
          + (p.carryover || 0) + ' преноса, ' + (p.adjustments || 0) + ' корекции.', true);
        (p.unmatched || []).forEach((u) => line('⚠ не се намира в чек листата: ' + (u.label || u)));
        (p.conflicts || []).forEach((c) => line('⚠ разминаване: ' + (c.label || c.name || '') + ' — ' + (c.message || '')));
        if (!(p.unmatched || []).length && !(p.conflicts || []).length) line('Всички редове се разпознават.');
      } else {
        toast('Внесени: ' + (p.clients || 0) + ' клиента.');
        closeModal();
        view($('#page'));
      }
    } catch (e) {
      clear(result);
      result.appendChild(el('div', { style: 'color:var(--red)', text: e.message }));
    }
  };

  // openModal иска ОБЕКТ ({ title, body, foot }) — извикан с два отделни
  // аргумента, диалогът получава undefined и мълчи. Точно така бутонът веднъж
  // „не правеше нищо“; затова има тест, който го НАТИСКА.
  openModal({
    title: 'Внос на история',
    body: el('div', {},
      field('Файл (JSON)', file),
      info,
      result),
    foot: el('div', { style: 'display:flex;gap:8px' },
      el('button', { class: 'btn sm', type: 'button', text: 'Прегледай', onClick: () => send('preview') }),
      el('button', {
        class: 'btn sm danger', type: 'button', text: 'Внеси',
        onClick: async () => {
          const ok = await confirmDialog(
            'Внасяне на историята в „Такси".\n\nЗаписват се клиенти, плащания и преноси. '
            + 'Ако вече има заредени клиенти, сървърът ще откаже, за да не се удвоят данните.',
            { okText: 'Да, внеси', danger: true }
          );
          if (ok) send('apply');
        },
      })),
  });
}
